var __defProp = Object.defineProperty;
var __name = (target, value) => __defProp(target, "name", { value, configurable: true });

// src/decorator.ts
import { InjectionToken, root } from "@sker/core";
function getAllNodeTypes() {
  const nodeMetadatas = root.get(NODE, []);
  return nodeMetadatas.map((metadata) => metadata.target);
}
__name(getAllNodeTypes, "getAllNodeTypes");
function findNodeType(name) {
  return getAllNodeTypes().find((type) => type.name === name);
}
__name(findNodeType, "findNodeType");
function resolveConstructor(target) {
  if (typeof target === "function") {
    return target;
  }
  const typeName = Reflect.get(target, "type");
  const type = findNodeType(typeName);
  if (type) return type;
  if (target && typeof target === "object" && typeof target.constructor === "function") {
    return target.constructor;
  }
  throw new Error("Workflow decorators expect to receive a class constructor or instance.");
}
__name(resolveConstructor, "resolveConstructor");
var NODE = new InjectionToken(`NODE`);
function Node(options = {}) {
  return (target) => {
    const ctor = resolveConstructor(target);
    root.set([
      {
        provide: NODE,
        useValue: {
          target: ctor,
          ...options
        },
        multi: true
      }
    ]);
  };
}
__name(Node, "Node");
var EDGE = new InjectionToken(`EDGE`);
var HANDLER_METHOD = new InjectionToken(`HANDLER_METHOD`);
function Handler(ast) {
  return (target, propertyKey, descriptor) => {
    const ctor = resolveConstructor(target);
    root.set([
      {
        provide: HANDLER_METHOD,
        multi: true,
        useValue: {
          ast,
          target: ctor,
          property: propertyKey
        }
      },
      {
        provide: ctor,
        useClass: ctor
      }
    ]);
    return descriptor;
  };
}
__name(Handler, "Handler");
var RENDER_METHOD = new InjectionToken(`RENDER_METHOD`);
function Render(ast) {
  return (target, propertyKey, descriptor) => {
    const ctor = resolveConstructor(target);
    root.set([
      {
        provide: RENDER_METHOD,
        multi: true,
        useValue: {
          ast,
          target: ctor,
          property: propertyKey
        }
      },
      {
        provide: ctor,
        useClass: ctor
      }
    ]);
    return descriptor;
  };
}
__name(Render, "Render");
var SETTING_METHOD = new InjectionToken(`SETTING_METHOD`);
function Setting(ast) {
  return (target, propertyKey, descriptor) => {
    const ctor = resolveConstructor(target);
    root.set([
      {
        provide: SETTING_METHOD,
        multi: true,
        useValue: {
          ast,
          target: ctor,
          property: propertyKey
        }
      },
      {
        provide: ctor,
        useClass: ctor
      }
    ]);
    return descriptor;
  };
}
__name(Setting, "Setting");
var PREVIEW_METHOD = new InjectionToken(`PREVIEW_METHOD`);
function Preview(ast) {
  return (target, propertyKey, descriptor) => {
    const ctor = resolveConstructor(target);
    root.set([
      {
        provide: PREVIEW_METHOD,
        multi: true,
        useValue: {
          ast,
          target: ctor,
          property: propertyKey
        }
      },
      {
        provide: ctor,
        useClass: ctor
      }
    ]);
    return descriptor;
  };
}
__name(Preview, "Preview");
var IS_MULTI = 1;
var IS_BUFFER = 16;
function hasMultiMode(mode) {
  return ((mode ?? 0) & IS_MULTI) === IS_MULTI;
}
__name(hasMultiMode, "hasMultiMode");
function hasBufferMode(mode) {
  return ((mode ?? 0) & IS_BUFFER) === IS_BUFFER;
}
__name(hasBufferMode, "hasBufferMode");
var INPUT = new InjectionToken(`INPUT`);
function Input(options) {
  return (target, propertyKey) => {
    const ctor = resolveConstructor(target);
    let mode = options?.mode;
    root.set([
      {
        provide: INPUT,
        multi: true,
        useValue: {
          target: ctor,
          propertyKey,
          mode,
          required: options?.required,
          defaultValue: options?.defaultValue,
          title: options?.title,
          type: options?.type,
          options: options?.options
        }
      }
    ]);
  };
}
__name(Input, "Input");
function getInputMetadata(target, propertyKey) {
  const ctor = resolveConstructor(target);
  const allInputs = root.get(INPUT, []);
  const targetInputs = allInputs.filter((it) => it.target === ctor);
  if (propertyKey !== void 0) {
    const metadata = targetInputs.find((it) => it.propertyKey === propertyKey);
    return metadata || {
      target: ctor,
      propertyKey,
      mode: 0,
      isMulti: false
    };
  }
  return targetInputs;
}
__name(getInputMetadata, "getInputMetadata");
var OUTPUT = new InjectionToken(`OUTPUT`);
function Output(options = {}) {
  return (target, propertyKey) => {
    const ctor = resolveConstructor(target);
    root.set([
      {
        provide: OUTPUT,
        multi: true,
        useValue: {
          target: ctor,
          propertyKey,
          ...options
        }
      }
    ]);
  };
}
__name(Output, "Output");
var STATE = new InjectionToken(`STATE`);
function State(options) {
  return (target, propertyKey) => {
    const ctor = resolveConstructor(target);
    root.set([
      {
        provide: STATE,
        multi: true,
        useValue: {
          target: ctor,
          type: options?.type,
          propertyKey,
          title: options?.title,
          defaultValue: options?.defaultValue
        }
      }
    ]);
  };
}
__name(State, "State");
function getStateMetadata(target, propertyKey) {
  const ctor = resolveConstructor(target);
  const allStates = root.get(STATE, []);
  const targetStates = allStates.filter((it) => it.target === ctor);
  if (propertyKey !== void 0) {
    const metadata = targetStates.find((it) => it.propertyKey === propertyKey);
    return metadata || {
      target: ctor,
      propertyKey
    };
  }
  return targetStates;
}
__name(getStateMetadata, "getStateMetadata");
var TOOL_METHOD = new InjectionToken(`TOOL_METHOD`);
function Tool(ast) {
  return (target, propertyKey, descriptor) => {
    const ctor = resolveConstructor(target);
    root.set([
      {
        provide: TOOL_METHOD,
        multi: true,
        useValue: {
          ast,
          target: ctor,
          property: propertyKey
        }
      },
      {
        provide: ctor,
        useClass: ctor
      }
    ]);
    return descriptor;
  };
}
__name(Tool, "Tool");
function getToolMethods(ast) {
  const allTools = root.get(TOOL_METHOD, []);
  return allTools.filter((tool) => tool.ast === ast);
}
__name(getToolMethods, "getToolMethods");
function hasTool(ast) {
  return getToolMethods(ast).length > 0;
}
__name(hasTool, "hasTool");
var DERIVED_INPUT = new InjectionToken(`DERIVED_INPUT`);
var DERIVED_OUTPUT = new InjectionToken(`DERIVED_OUTPUT`);

// src/dynamic-node-registry.ts
import { root as root2 } from "@sker/core";
var DynamicNodeRegistry = class {
  static {
    __name(this, "DynamicNodeRegistry");
  }
  /**
  * 注册派生节点
  */
  static register(params) {
    const BaseClass = findNodeType(params.baseType);
    if (!BaseClass) {
      throw new Error(`\u57FA\u7C7B\u8282\u70B9\u4E0D\u5B58\u5728: ${params.baseType}`);
    }
    const DerivedClass = this.createDerivedClass(params.name, BaseClass, params.frozenInputs);
    this.registerNode(DerivedClass, params.nodeMetadata);
    return DerivedClass;
  }
  /**
  * 创建派生类
  */
  static createDerivedClass(name, BaseClass, frozenInputs) {
    const DerivedClass = class extends BaseClass {
      static {
        __name(this, "DerivedClass");
      }
      constructor() {
        super();
        Object.assign(this, frozenInputs);
      }
    };
    Object.defineProperty(DerivedClass, "name", {
      value: name
    });
    return DerivedClass;
  }
  /**
  * 注册节点到全局注册表
  */
  static registerNode(DerivedClass, nodeMetadata) {
    const metadata = {
      target: DerivedClass,
      title: nodeMetadata.class.title,
      type: nodeMetadata.class.type
    };
    root2.set([
      {
        provide: NODE,
        useValue: metadata,
        multi: true
      }
    ]);
    if (nodeMetadata.inputs) {
      root2.set([
        {
          provide: DERIVED_INPUT,
          useValue: {
            target: DerivedClass,
            inputs: nodeMetadata.inputs
          },
          multi: true
        }
      ]);
    }
    if (nodeMetadata.outputs) {
      root2.set([
        {
          provide: DERIVED_OUTPUT,
          useValue: {
            target: DerivedClass,
            outputs: nodeMetadata.outputs
          },
          multi: true
        }
      ]);
    }
  }
  /**
  * 获取所有已注册的派生节点
  */
  static getAll() {
    return root2.get(NODE, []);
  }
};

// src/utils.ts
import { Observable } from "rxjs";
function generateId() {
  return v4();
}
__name(generateId, "generateId");
function isINode(val) {
  return typeof val === "object" && val !== null && "type" in val;
}
__name(isINode, "isINode");
function isObservable(val) {
  return val && Reflect.get(val, "subscribe");
}
__name(isObservable, "isObservable");
var clone = /* @__PURE__ */ __name((obj, seen = /* @__PURE__ */ new WeakSet()) => {
  if (obj === null || typeof obj !== "object") return obj;
  if (seen.has(obj)) return void 0;
  if (obj instanceof Observable || typeof obj === "function") {
    return void 0;
  }
  seen.add(obj);
  if (Array.isArray(obj)) {
    return obj.map((item) => clone(item, seen)).filter((item) => item !== void 0);
  }
  const cloned = {};
  for (const key in obj) {
    if (obj.hasOwnProperty(key)) {
      const value = clone(obj[key], seen);
      if (value !== void 0) {
        cloned[key] = value;
      }
    }
  }
  return cloned;
}, "clone");
var IDX = 256;
var HEX = [];
var BUFFER;
while (IDX--) HEX[IDX] = (IDX + 256).toString(16).substring(1);
function v4() {
  let i = 0;
  let num;
  let out = "";
  if (!BUFFER || IDX + 16 > 256) {
    BUFFER = Array(i = 256);
    while (i--) BUFFER[i] = 256 * Math.random() | 0;
    i = IDX = 0;
  }
  for (; i < 16; i++) {
    num = BUFFER[IDX + i];
    if (i === 6) out += HEX[num & 15 | 64];
    else if (i === 8) out += HEX[num & 63 | 128];
    else out += HEX[num];
    if (i & 1 && i > 1 && i < 11) out += "-";
  }
  IDX++;
  return out;
}
__name(v4, "v4");

// src/ast.ts
import { BehaviorSubject } from "rxjs";

// src/ast-utils.ts
import { ErrorSerializer, root as root3 } from "@sker/core";
function setAstError(node, error, includeStack = false) {
  console.error(`\u274C [${node.type}] \u8282\u70B9\u6267\u884C\u5931\u8D25:`, {
    nodeId: node.id,
    nodeName: node.name,
    error: error instanceof Error ? error.message : String(error)
  });
  node.error = ErrorSerializer.serialize(error, includeStack);
}
__name(setAstError, "setAstError");
function getAstDeepError(node) {
  return node.error ? ErrorSerializer.extractDeepestError(node.error) : void 0;
}
__name(getAstDeepError, "getAstDeepError");
function getAstErrorDescription(node) {
  return node.error ? ErrorSerializer.getFullDescription(node.error) : void 0;
}
__name(getAstErrorDescription, "getAstErrorDescription");
function isWorkflowGroup(workflow) {
  return workflow.type === `WorkflowGraphAst`;
}
__name(isWorkflowGroup, "isWorkflowGroup");
function hasNode(nodes, id) {
  for (const node of nodes) {
    if (node.id === id) return true;
    if (node.isGroupNode && node.nodes?.length > 0) {
      if (hasNode(node.nodes, id)) return true;
    }
  }
  return false;
}
__name(hasNode, "hasNode");
function getNodeById(nodes, id) {
  for (const node of nodes) {
    if (node.id === id) return node;
    if (node.isGroupNode && node.nodes?.length > 0) {
      const found = getNodeById(node.nodes, id);
      if (found) return found;
    }
  }
  return void 0;
}
__name(getNodeById, "getNodeById");
function getNodesByType(nodes, type) {
  return nodes.filter((node) => node.type === type);
}
__name(getNodesByType, "getNodesByType");
function addNode(nodes, node) {
  if (hasNode(nodes, node.id)) {
    throw new Error(`\u8282\u70B9ID\u5DF2\u5B58\u5728: ${node.id}`);
  }
  return [
    ...nodes,
    node
  ];
}
__name(addNode, "addNode");
function addNodes(existingNodes, newNodes) {
  const duplicateIds = newNodes.filter((node) => hasNode(existingNodes, node.id)).map((node) => node.id);
  if (duplicateIds.length > 0) {
    throw new Error(`\u4EE5\u4E0B\u8282\u70B9ID\u5DF2\u5B58\u5728: ${duplicateIds.join(", ")}`);
  }
  return [
    ...existingNodes,
    ...newNodes
  ];
}
__name(addNodes, "addNodes");
function updateNode(nodes, id, updates) {
  const node = getNodeById(nodes, id);
  if (!node) {
    throw new Error(`\u8282\u70B9\u4E0D\u5B58\u5728: ${id}`);
  }
  return nodes.map((n) => n.id === id ? {
    ...n,
    ...updates
  } : n);
}
__name(updateNode, "updateNode");
function updateNodePosition(nodes, id, position) {
  const node = getNodeById(nodes, id);
  if (!node) {
    throw new Error(`\u8282\u70B9\u4E0D\u5B58\u5728: ${id}`);
  }
  return nodes.map((n) => {
    if (n.id !== id) return n;
    const newPosition = {
      ...n.position
    };
    if (position.x !== void 0) newPosition.x = position.x;
    if (position.y !== void 0) newPosition.y = position.y;
    return {
      ...n,
      position: newPosition
    };
  });
}
__name(updateNodePosition, "updateNodePosition");
function removeNode(nodes, id) {
  const node = getNodeById(nodes, id);
  if (!node) {
    return {
      nodes,
      removed: void 0
    };
  }
  return {
    nodes: nodes.filter((n) => n.id !== id),
    removed: node
  };
}
__name(removeNode, "removeNode");
function removeNodes(nodes, ids) {
  const removed = [];
  const remaining = nodes.filter((node) => {
    if (ids.includes(node.id)) {
      removed.push(node);
      return false;
    }
    return true;
  });
  return {
    nodes: remaining,
    removed
  };
}
__name(removeNodes, "removeNodes");
function hasEdge(edges, fromOrId, to, fromProperty, toProperty) {
  if (to === void 0) {
    return edges.some((edge) => edge.id === fromOrId);
  }
  return edges.some((edge) => {
    if (edge.from !== fromOrId || edge.to !== to) return false;
    if (fromProperty !== void 0 || toProperty !== void 0) {
      return edge.fromProperty === fromProperty && edge.toProperty === toProperty;
    }
    return true;
  });
}
__name(hasEdge, "hasEdge");
function getEdgeById(edges, id) {
  return edges.find((edge) => edge.id === id);
}
__name(getEdgeById, "getEdgeById");
function getEdgesByNode(edges, nodeId, direction = "all") {
  return edges.filter((edge) => {
    if (direction === "in") return edge.to === nodeId;
    if (direction === "out") return edge.from === nodeId;
    return edge.from === nodeId || edge.to === nodeId;
  });
}
__name(getEdgesByNode, "getEdgesByNode");
function addEdge(nodes, edges, edge) {
  if (!hasNode(nodes, edge.from)) {
    throw new Error(`\u8FB9\u7684\u8D77\u59CB\u8282\u70B9\u4E0D\u5B58\u5728: ${edge.from}`);
  }
  if (!hasNode(nodes, edge.to)) {
    throw new Error(`\u8FB9\u7684\u76EE\u6807\u8282\u70B9\u4E0D\u5B58\u5728: ${edge.to}`);
  }
  if (hasEdge(edges, edge.from, edge.to, edge.fromProperty, edge.toProperty)) {
    throw new Error(`\u8FB9\u5DF2\u5B58\u5728: ${edge.from}.${edge.fromProperty || "*"} -> ${edge.to}.${edge.toProperty || "*"}`);
  }
  return [
    ...edges,
    edge
  ];
}
__name(addEdge, "addEdge");
function addEdges(nodes, edges, newEdges) {
  for (const edge of newEdges) {
    if (!hasNode(nodes, edge.from)) {
      throw new Error(`\u8FB9\u7684\u8D77\u59CB\u8282\u70B9\u4E0D\u5B58\u5728: ${edge.from}`);
    }
    if (!hasNode(nodes, edge.to)) {
      throw new Error(`\u8FB9\u7684\u76EE\u6807\u8282\u70B9\u4E0D\u5B58\u5728: ${edge.to}`);
    }
  }
  return [
    ...edges,
    ...newEdges
  ];
}
__name(addEdges, "addEdges");
function addConditionalEdge(nodes, edges, edge) {
  if (!edge.condition) {
    throw new Error("\u6761\u4EF6\u8FB9\u5FC5\u987B\u5305\u542Bcondition\u5C5E\u6027");
  }
  return addEdge(nodes, edges, edge);
}
__name(addConditionalEdge, "addConditionalEdge");
function updateEdge(nodes, edges, id, updates) {
  const edge = getEdgeById(edges, id);
  if (!edge) {
    throw new Error(`\u8FB9\u4E0D\u5B58\u5728: ${id}`);
  }
  if (updates.from && !hasNode(nodes, updates.from)) {
    throw new Error(`\u8FB9\u7684\u8D77\u59CB\u8282\u70B9\u4E0D\u5B58\u5728: ${updates.from}`);
  }
  if (updates.to && !hasNode(nodes, updates.to)) {
    throw new Error(`\u8FB9\u7684\u76EE\u6807\u8282\u70B9\u4E0D\u5B58\u5728: ${updates.to}`);
  }
  return edges.map((e) => e.id === id ? {
    ...e,
    ...updates
  } : e);
}
__name(updateEdge, "updateEdge");
function updateEdgeCondition(edges, id, condition) {
  const edge = getEdgeById(edges, id);
  if (!edge) {
    throw new Error(`\u8FB9\u4E0D\u5B58\u5728: ${id}`);
  }
  return edges.map((e) => e.id === id ? {
    ...e,
    condition
  } : e);
}
__name(updateEdgeCondition, "updateEdgeCondition");
function removeEdge(edges, id) {
  const edge = getEdgeById(edges, id);
  if (!edge) {
    return {
      edges,
      removed: void 0
    };
  }
  return {
    edges: edges.filter((e) => e.id !== id),
    removed: edge
  };
}
__name(removeEdge, "removeEdge");
function removeEdgesByNode(edges, nodeId, direction = "all") {
  const removed = [];
  const remaining = edges.filter((edge) => {
    const shouldRemove = direction === "in" && edge.to === nodeId || direction === "out" && edge.from === nodeId || direction === "all" && (edge.from === nodeId || edge.to === nodeId);
    if (shouldRemove) {
      removed.push(edge);
      return false;
    }
    return true;
  });
  return {
    edges: remaining,
    removed
  };
}
__name(removeEdgesByNode, "removeEdgesByNode");
function validateGraph(nodes, edges) {
  const errors = [];
  for (const edge of edges) {
    if (!hasNode(nodes, edge.from)) {
      errors.push(`\u8FB9\u7684\u8D77\u59CB\u8282\u70B9\u4E0D\u5B58\u5728: ${edge.from} (\u8FB9ID: ${edge.id})`);
    }
    if (!hasNode(nodes, edge.to)) {
      errors.push(`\u8FB9\u7684\u76EE\u6807\u8282\u70B9\u4E0D\u5B58\u5728: ${edge.to} (\u8FB9ID: ${edge.id})`);
    }
  }
  const connectedNodeIds = /* @__PURE__ */ new Set();
  for (const edge of edges) {
    connectedNodeIds.add(edge.from);
    connectedNodeIds.add(edge.to);
  }
  const isolatedNodes = nodes.filter((node) => !connectedNodeIds.has(node.id));
  if (isolatedNodes.length > 0) {
    errors.push(`\u53D1\u73B0\u5B64\u7ACB\u8282\u70B9: ${isolatedNodes.map((n) => n.id).join(", ")}`);
  }
  return {
    isValid: errors.length === 0,
    errors
  };
}
__name(validateGraph, "validateGraph");
function clearGraph(nodes, edges, options = {
  nodes: true,
  edges: true
}) {
  return {
    nodes: options.nodes ? [] : nodes,
    edges: options.edges ? [] : edges,
    previous: {
      nodes: options.nodes ? [
        ...nodes
      ] : [],
      edges: options.edges ? [
        ...edges
      ] : []
    }
  };
}
__name(clearGraph, "clearGraph");
function cleanOrphanedProperties(workflow, deletedNodeIds) {
  const validNodeIds = deletedNodeIds ? new Set(workflow.nodes?.map((n) => n.id).filter((id) => !deletedNodeIds.includes(id))) : new Set(workflow.nodes?.map((n) => n.id) || []);
  const builtinProps = /* @__PURE__ */ new Set([
    "id",
    "type",
    "name",
    "description",
    "state",
    "error",
    "count",
    "emitCount",
    "position",
    "color",
    "collapsed",
    "width",
    "nodes",
    "edges",
    "entryNodeIds",
    "viewport",
    "tags",
    "abortSignal"
  ]);
  Object.keys(workflow).forEach((key) => {
    if (builtinProps.has(key)) {
      return;
    }
    const lastDotIndex = key.lastIndexOf(".");
    if (lastDotIndex === -1) {
      return;
    }
    const nodeId = key.substring(0, lastDotIndex);
    if (deletedNodeIds && !deletedNodeIds.includes(nodeId)) {
      return;
    }
    if (!validNodeIds.has(nodeId)) {
      delete workflow[key];
    }
  });
}
__name(cleanOrphanedProperties, "cleanOrphanedProperties");
function getExposedInputs(nodes, edges) {
  const exposedInputs = [];
  for (const node of nodes) {
    const isConnected = edges.some((edge) => edge.to === node.id);
    if (isConnected) continue;
    if (node.type === "WorkflowGraphAst") continue;
    try {
      const ctor = findNodeType(node.type);
      if (!ctor) continue;
      const inputMetadatas = root3.get(INPUT, []);
      const nodeInputs = inputMetadatas.filter((meta) => meta.target === ctor);
      for (const inputMeta of nodeInputs) {
        const property = String(inputMeta.propertyKey);
        if (!isConnected) {
          exposedInputs.push({
            nodeId: node.id,
            property,
            title: inputMeta.title || property,
            type: inputMeta.type,
            required: inputMeta.required
          });
        }
      }
    } catch (error) {
      continue;
    }
  }
  return exposedInputs;
}
__name(getExposedInputs, "getExposedInputs");
function getExposedOutputs(nodes, edges) {
  const exposedOutputs = [];
  for (const node of nodes) {
    if (node.type === "WorkflowGraphAst") continue;
    try {
      const registry = root3.get(NODE, []);
      const nodeMetadata = registry.find((meta) => meta.target.name === node.type);
      const ctor = nodeMetadata?.target;
      if (!ctor) continue;
      const outputMetadatas = root3.get(OUTPUT, []);
      const nodeOutputs = outputMetadatas.filter((meta) => meta.target === ctor);
      for (const outputMeta of nodeOutputs) {
        const property = String(outputMeta.propertyKey);
        const isConnected = edges.some((edge) => edge.from === node.id && edge.fromProperty === property);
        if (!isConnected) {
          exposedOutputs.push({
            nodeId: node.id,
            property,
            title: outputMeta.title || property,
            type: outputMeta.type
          });
        }
      }
    } catch (error) {
      continue;
    }
  }
  return exposedOutputs;
}
__name(getExposedOutputs, "getExposedOutputs");
function getEntryNodeInputs(nodes, edges, entryNodeIds) {
  const inputs = [];
  const entryNodes = entryNodeIds && entryNodeIds.length > 0 ? nodes.filter((node) => entryNodeIds.includes(node.id)) : nodes.filter((node) => {
    const hasIncomingEdges = edges.some((edge) => edge.to === node.id);
    return !hasIncomingEdges;
  });
  for (const node of entryNodes) {
    if (node.type === "WorkflowGraphAst") continue;
    try {
      const ctor = findNodeType(node.type);
      if (!ctor) continue;
      const inputMetadatas = root3.get(INPUT, []);
      const nodeInputs = inputMetadatas.filter((meta) => meta.target === ctor);
      for (const inputMeta of nodeInputs) {
        const property = String(inputMeta.propertyKey);
        inputs.push({
          nodeId: node.id,
          property,
          title: inputMeta.title || property,
          type: inputMeta.type,
          required: inputMeta.required,
          defaultValue: inputMeta.defaultValue ?? node[property]
        });
      }
    } catch (error) {
      console.warn(`[getEntryNodeInputs] \u83B7\u53D6\u8282\u70B9 ${node.id} \u8F93\u5165\u5143\u6570\u636E\u5931\u8D25:`, error);
      continue;
    }
  }
  return inputs;
}
__name(getEntryNodeInputs, "getEntryNodeInputs");
function extractEndNodeOutputs(nodes, endNodeIds) {
  const outputs = {};
  if (!endNodeIds || endNodeIds.length === 0) {
    return outputs;
  }
  const endNodes = nodes.filter((node) => endNodeIds.includes(node.id));
  for (const node of endNodes) {
    if (node.type === "WorkflowGraphAst") continue;
    if (node.state !== "success") {
      console.warn(`[extractEndNodeOutputs] \u7ED3\u675F\u8282\u70B9 ${node.id} \u72B6\u6001\u4E3A ${node.state}\uFF0C\u8DF3\u8FC7\u8F93\u51FA\u63D0\u53D6`);
      continue;
    }
    try {
      const ctor = findNodeType(node.type);
      if (!ctor) continue;
      const outputMetadatas = root3.get(OUTPUT, []);
      const nodeOutputs = outputMetadatas.filter((meta) => meta.target === ctor);
      for (const outputMeta of nodeOutputs) {
        const property = String(outputMeta.propertyKey);
        const value = node[property];
        if (value !== void 0) {
          const outputKey = `${node.id}.${property}`;
          outputs[outputKey] = value;
        }
      }
    } catch (error) {
      console.warn(`[extractEndNodeOutputs] \u63D0\u53D6\u8282\u70B9 ${node.id} \u8F93\u51FA\u65F6\u51FA\u9519:`, error);
      continue;
    }
  }
  return outputs;
}
__name(extractEndNodeOutputs, "extractEndNodeOutputs");
function resetNodeToDefaults(node) {
  try {
    if (node.metadata?.class?.stateful) {
      console.log(`[resetNodeToDefaults] \u8282\u70B9 ${node.id} (${node.type}) \u6807\u8BB0\u4E3A stateful\uFF0C\u8DF3\u8FC7\u91CD\u7F6E`);
      return;
    }
    const nodeInputs = node.metadata?.inputs || [];
    for (const inputMeta of nodeInputs) {
      const property = String(inputMeta.property);
      const defaultValue = inputMeta.defaultValue;
      if (defaultValue !== void 0) {
        node[property] = cloneDefaultValue(defaultValue);
      }
    }
    const nodeOutputs = node.metadata?.outputs || [];
    for (const outputMeta of nodeOutputs) {
      const property = String(outputMeta.property);
      const defaultValue = outputMeta.defaultValue;
      if (defaultValue !== void 0) {
        node[property] = cloneDefaultValue(defaultValue);
      }
    }
  } catch (error) {
    console.warn(`[resetNodeToDefaults] \u91CD\u7F6E\u8282\u70B9 ${node.id} \u9ED8\u8BA4\u503C\u5931\u8D25:`, error);
  }
}
__name(resetNodeToDefaults, "resetNodeToDefaults");
function cloneDefaultValue(value) {
  if (value === null || value === void 0) {
    return value;
  }
  if (typeof value !== "object") {
    return value;
  }
  if (value instanceof Date) {
    return new Date(value);
  }
  if (Array.isArray(value)) {
    return value.map((item) => cloneDefaultValue(item));
  }
  try {
    if (typeof structuredClone !== "undefined") {
      return structuredClone(value);
    }
  } catch {
  }
  try {
    return JSON.parse(JSON.stringify(value));
  } catch {
    return value;
  }
}
__name(cloneDefaultValue, "cloneDefaultValue");

// src/ast.ts
function _ts_decorate(decorators, target, key, desc) {
  var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
  else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  return c > 3 && r && Object.defineProperty(target, key, r), r;
}
__name(_ts_decorate, "_ts_decorate");
function _ts_metadata(k, v) {
  if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
__name(_ts_metadata, "_ts_metadata");
var Ast = class {
  static {
    __name(this, "Ast");
  }
  count = 0;
  emitCount = 0;
  id = generateId();
  name;
  description;
  color;
  collapsed;
  width;
  height;
  state = "pending";
  error;
  type;
  position = {
    x: 0,
    y: 0
  };
  parentId;
  metadata;
  toJSON() {
    const result = {};
    for (const key in this) {
      if (!Object.prototype.hasOwnProperty.call(this, key)) continue;
      const value = this[key];
      if (value instanceof BehaviorSubject || value instanceof Map) {
        continue;
      }
      result[key] = value;
    }
    return result;
  }
};
var WorkflowGraphAst = class extends Ast {
  static {
    __name(this, "WorkflowGraphAst");
  }
  name;
  // 节点列表
  nodes = [];
  // 开始节点
  entryNodeIds = [];
  // 结束节点
  endNodeIds = [];
  // 边集合
  edges = [];
  toolNodeIds = [];
  viewport;
  collapsed = false;
  tags = [];
  // 显式标记是否为分组节点
  isGroupNode = false;
  type = `WorkflowGraphAst`;
  abortSignal;
  get isGroup() {
    return this.isGroupNode === true;
  }
};
_ts_decorate([
  State({
    title: "\u540D\u79F0",
    type: "text"
  }),
  _ts_metadata("design:type", Object)
], WorkflowGraphAst.prototype, "name", void 0);
_ts_decorate([
  State({
    title: "\u8282\u70B9\u5217\u8868"
  }),
  _ts_metadata("design:type", Array)
], WorkflowGraphAst.prototype, "nodes", void 0);
_ts_decorate([
  State({
    title: "\u5F00\u59CB\u8282\u70B9"
  }),
  _ts_metadata("design:type", Array)
], WorkflowGraphAst.prototype, "entryNodeIds", void 0);
_ts_decorate([
  State({
    title: "\u7ED3\u675F\u8282\u70B9"
  }),
  _ts_metadata("design:type", Array)
], WorkflowGraphAst.prototype, "endNodeIds", void 0);
_ts_decorate([
  State({
    title: "\u8FB9\u5217\u8868"
  }),
  _ts_metadata("design:type", Array)
], WorkflowGraphAst.prototype, "edges", void 0);
_ts_decorate([
  State({
    title: "\u5DE5\u5177\u8282\u70B9ID\u5217\u8868"
  }),
  _ts_metadata("design:type", Array)
], WorkflowGraphAst.prototype, "toolNodeIds", void 0);
_ts_decorate([
  State({
    title: "\u89C6\u56FE\u72B6\u6001"
  }),
  _ts_metadata("design:type", Object)
], WorkflowGraphAst.prototype, "viewport", void 0);
_ts_decorate([
  State({
    title: "\u6298\u53E0\u72B6\u6001"
  }),
  _ts_metadata("design:type", Boolean)
], WorkflowGraphAst.prototype, "collapsed", void 0);
_ts_decorate([
  State({
    title: "\u6807\u7B7E"
  }),
  _ts_metadata("design:type", Array)
], WorkflowGraphAst.prototype, "tags", void 0);
_ts_decorate([
  State({
    title: "\u5206\u7EC4\u6807\u8BB0"
  }),
  _ts_metadata("design:type", Boolean)
], WorkflowGraphAst.prototype, "isGroupNode", void 0);
WorkflowGraphAst = _ts_decorate([
  Node({
    title: "\u5DE5\u4F5C\u6D41",
    type: "basic"
  })
], WorkflowGraphAst);
function createWorkflowGraphAst({ nodes = [], edges = [], id, state, name = "Untitled Workflow", entryNodeIds = [], endNodeIds = [] } = {}) {
  const ast = new WorkflowGraphAst();
  ast.name = name;
  ast.nodes = nodes;
  ast.edges = edges;
  ast.entryNodeIds = entryNodeIds;
  ast.endNodeIds = endNodeIds;
  if (id) ast.id = id;
  if (state) ast.state = state;
  return ast;
}
__name(createWorkflowGraphAst, "createWorkflowGraphAst");
function isWorkflowGraphAst(ast) {
  return ast?.type === `WorkflowGraphAst`;
}
__name(isWorkflowGraphAst, "isWorkflowGraphAst");

// src/compiler/index.ts
import { Injectable, root as root4 } from "@sker/core";

// src/types.ts
var ROUTE_SKIPPED = `__ROUTE_SKIPPED`;
function isRouteSkipped(value) {
  return value === ROUTE_SKIPPED;
}
__name(isRouteSkipped, "isRouteSkipped");
function extractSubjectValue(value) {
  return value;
}
__name(extractSubjectValue, "extractSubjectValue");
function isNode(val) {
  return val && val.metadata;
}
__name(isNode, "isNode");
var EdgeMode = /* @__PURE__ */ (function(EdgeMode2) {
  EdgeMode2["MERGE"] = "merge";
  EdgeMode2["ZIP"] = "zip";
  EdgeMode2["COMBINE_LATEST"] = "combineLatest";
  EdgeMode2["WITH_LATEST_FROM"] = "withLatestFrom";
  return EdgeMode2;
})({});
var EDGE_MODE_PRIORITY = {
  ["merge"]: 1,
  ["combineLatest"]: 2,
  ["withLatestFrom"]: 3,
  ["zip"]: 4
};

// src/compiler/index.ts
function _ts_decorate2(decorators, target, key, desc) {
  var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
  else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  return c > 3 && r && Object.defineProperty(target, key, r), r;
}
__name(_ts_decorate2, "_ts_decorate");
var Compiler = class {
  static {
    __name(this, "Compiler");
  }
  /**
   * 编译 AST 为 INode
   */
  compile(ast) {
    if (isNode(ast)) {
      return ast;
    }
    const ctor = findNodeType(ast.type);
    if (!ctor) {
      console.log(ast);
      throw new Error(`compiler error: ast type ${ast.type} not found`);
    }
    const instance = ast instanceof ctor ? ast : new ctor();
    if (instance !== ast) {
      for (const [key, value] of Object.entries(ast)) {
        instance[key] = value;
      }
    }
    const classMetadata = this.extractNodeMetadata(ctor);
    const staticInputs = this.extractInputMetadata(ctor);
    const staticOutputs = this.extractOutputMetadata(ctor, instance);
    const states = this.extractStateMetadata(ctor);
    instance.metadata = {
      type: ctor.name,
      class: classMetadata,
      inputs: staticInputs,
      outputs: staticOutputs,
      states
    };
    return instance;
  }
  /**
   * 提取 @Node 类装饰器元数据
   */
  extractNodeMetadata(ctor) {
    const allNodeMetadata = root4.get(NODE, []);
    const metadata = allNodeMetadata.find((m) => m.target === ctor);
    return {
      title: metadata?.title,
      type: metadata?.type,
      dynamicInputs: metadata?.dynamicInputs,
      dynamicOutputs: metadata?.dynamicOutputs,
      errorStrategy: metadata?.errorStrategy,
      maxRetries: metadata?.maxRetries,
      retryDelay: metadata?.retryDelay,
      retryBackoff: metadata?.retryBackoff,
      stateful: metadata?.stateful,
      hasTool: hasTool(ctor)
    };
  }
  /**
   * 提取 @Input 属性装饰器元数据
   * 优先查找派生元数据，未找到时回退到基类装饰器
   */
  extractInputMetadata(ctor) {
    const allDerivedInputs = root4.get(DERIVED_INPUT, []);
    const derivedMetadata = allDerivedInputs.find((m) => m.target === ctor);
    if (derivedMetadata) {
      return derivedMetadata.inputs.map((input) => ({
        ...input,
        isStatic: true
      }));
    }
    const allInputMetadata = root4.get(INPUT, []);
    const targetInputs = allInputMetadata.filter((m) => m.target === ctor);
    return targetInputs.map((input) => ({
      property: String(input.propertyKey),
      mode: input.mode,
      isMulti: hasMultiMode(input.mode),
      isBuffer: hasBufferMode(input.mode),
      required: input.required,
      defaultValue: input.defaultValue,
      title: input.title,
      type: input.type,
      options: input.options,
      isStatic: true
    }));
  }
  /**
   * 提取 @Output 属性装饰器元数据
   * 优先查找派生元数据，未找到时回退到基类装饰器
   *
   * 增强：检测 BehaviorSubject 类型的属性并标记 isSubject
   */
  extractOutputMetadata(ctor, instance) {
    const allDerivedOutputs = root4.get(DERIVED_OUTPUT, []);
    const derivedMetadata = allDerivedOutputs.find((m) => m.target === ctor);
    if (derivedMetadata) {
      return derivedMetadata.outputs.map((output) => ({
        ...output,
        isStatic: true,
        isSubject: false
      }));
    }
    const allOutputMetadata = root4.get(OUTPUT, []);
    const targetOutputs = allOutputMetadata.filter((m) => m.target === ctor);
    return targetOutputs.map((output) => {
      const key = String(output.propertyKey);
      const isSubject = false;
      return {
        property: key,
        title: output.title,
        type: output.type,
        isRouter: output.isRouter,
        dynamic: output.dynamic,
        condition: output.condition,
        isStatic: true,
        isSubject
      };
    });
  }
  /**
   * 提取 @State 属性装饰器元数据
   */
  extractStateMetadata(ctor) {
    const allStateMetadata = root4.get(STATE, []);
    const targetStates = allStateMetadata.filter((m) => m.target === ctor);
    return targetStates.map((state) => ({
      propertyKey: state.propertyKey,
      title: state.title,
      type: state.type,
      defaultValue: state.defaultValue
    }));
  }
};
Compiler = _ts_decorate2([
  Injectable()
], Compiler);

// src/generate.ts
import { root as root5 } from "@sker/core";
function fromJson(json) {
  if (!json) return json;
  const compiler = root5.get(Compiler);
  if (json.type === "WorkflowGraphAst") {
    const ast = new WorkflowGraphAst();
    Object.assign(ast, json);
    if (Array.isArray(json.nodes)) {
      ast.nodes = json.nodes.map((node) => {
        if (node.type === "WorkflowGraphAst") {
          return fromJson(node);
        }
        return compiler.compile(node);
      });
    }
    return ast;
  } else if (json.type) {
    return compiler.compile(json);
  }
  return json;
}
__name(fromJson, "fromJson");
function toJson(ast) {
  return clone(ast);
}
__name(toJson, "toJson");

// src/executor.ts
import { Inject, Injectable as Injectable4, root as root7 } from "@sker/core";
import { defer, of as of2 } from "rxjs";

// src/execution/visitor-executor.ts
import { Injectable as Injectable3, root as root6 } from "@sker/core";
import { Observable as Observable3, of, from as from2 } from "rxjs";
import { catchError, switchMap, tap } from "rxjs/operators";

// src/defaultVisitor.ts
import { isObservable as isObservable2, Observable as Observable2 } from "rxjs";
import { InjectionToken as InjectionToken2, Injectable as Injectable2 } from "@sker/core";
function _ts_decorate3(decorators, target, key, desc) {
  var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
  else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  return c > 3 && r && Object.defineProperty(target, key, r), r;
}
__name(_ts_decorate3, "_ts_decorate");
var DEFAULT_VISITOR = new InjectionToken2("DEFAULT_VISITOR");
var DefaultVisitor = class {
  static {
    __name(this, "DefaultVisitor");
  }
  visit(ast, input$, workflow) {
    return new Observable2((obs) => {
      console.log(`DefaultVisitor run ${ast.type}`);
      if (!input$) throw new Error(`[DefaultVisitor.handler] input$ is empty`);
      if (!isObservable2(input$)) throw new Error(`[DefaultVisitor.handler] input$ must be an Observable`);
      ast.state = "running";
      obs.next({
        type: "node_runing",
        id: ast.id
      });
      input$.subscribe({
        next: /* @__PURE__ */ __name((data) => {
          const emitData = {};
          ast.metadata?.inputs.forEach((input) => {
            ast.metadata?.outputs.forEach((output) => {
              ast[output.property] = data[input.property];
              emitData[output.property] = ast[output.property];
            });
          });
          obs.next({
            type: "node_emit",
            id: ast.id,
            data: emitData
          });
        }, "next"),
        error: /* @__PURE__ */ __name((error) => {
          ast.state = "fail";
          setAstError(ast, error);
          obs.next({
            type: "node_fail",
            id: ast.id,
            error: ast.error?.message
          });
        }, "error"),
        complete: /* @__PURE__ */ __name(() => {
          ast.state = "success";
          obs.next({
            type: "node_success",
            id: ast.id
          });
          obs.complete();
        }, "complete")
      });
    });
  }
};
DefaultVisitor = _ts_decorate3([
  Injectable2()
], DefaultVisitor);

// src/event-store/types.ts
var EVENT_STORE = /* @__PURE__ */ Symbol("EVENT_STORE");

// src/event-store/memory.ts
var MemoryEventStore = class {
  static {
    __name(this, "MemoryEventStore");
  }
  store = /* @__PURE__ */ new Map();
  async append(runId, event) {
    if (!this.store.has(runId)) {
      this.store.set(runId, []);
    }
    this.store.get(runId).push(event);
  }
  async getEvents(runId) {
    return this.store.get(runId) ?? [];
  }
  async getSuccessNodeIds(runId) {
    const events = this.store.get(runId) ?? [];
    return new Set(events.filter((e) => e.type === "node_success" && e.id).map((e) => e.id));
  }
  async getRunState(runId) {
    const events = this.store.get(runId) ?? [];
    const nodeLastEvent = /* @__PURE__ */ new Map();
    for (const event of events) {
      if (event.id && event.type !== "node_emit") {
        nodeLastEvent.set(event.id, event);
      }
    }
    const successNodeIds = /* @__PURE__ */ new Set();
    const failedNodeIds = /* @__PURE__ */ new Set();
    const runningNodeIds = /* @__PURE__ */ new Set();
    for (const [nodeId, event] of nodeLastEvent) {
      switch (event.type) {
        case "node_success":
          successNodeIds.add(nodeId);
          break;
        case "node_fail":
          failedNodeIds.add(nodeId);
          break;
        case "node_runing":
          runningNodeIds.add(nodeId);
          break;
      }
    }
    return {
      runId,
      successNodeIds,
      failedNodeIds,
      runningNodeIds
    };
  }
  async clear(runId) {
    this.store.delete(runId);
  }
  /** 清除所有数据（测试用） */
  clearAll() {
    this.store.clear();
  }
};

// src/event-store/event-stream.ts
import { BehaviorSubject as BehaviorSubject2, combineLatest, from } from "rxjs";
import { map, concatMap, delay } from "rxjs/operators";
var WorkflowEventStream = class _WorkflowEventStream {
  static {
    __name(this, "WorkflowEventStream");
  }
  /** 完整事件流 */
  _events$ = new BehaviorSubject2([]);
  /** 最大事件数（循环覆盖） */
  static MAX_EVENTS = 1e3;
  /** 回放索引（-1 = 实时模式，显示所有事件） */
  _replayIndex$ = new BehaviorSubject2(-1);
  /** 事件存储开关（默认关闭） */
  _storeEnabled$ = new BehaviorSubject2(false);
  /** 追加事件 */
  emit(event) {
    if (!this._storeEnabled$.value) return;
    const currentEvents = this._events$.value;
    const newEvents = currentEvents.length >= _WorkflowEventStream.MAX_EVENTS ? [
      ...currentEvents.slice(1),
      event
    ] : [
      ...currentEvents,
      event
    ];
    this._events$.next(newEvents);
  }
  /** 批量追加事件（恢复时使用） */
  restore(events) {
    this._events$.next(events);
  }
  /** 清空事件 */
  clear() {
    this._events$.next([]);
    this._replayIndex$.next(-1);
  }
  /** 获取所有事件 */
  get events() {
    return this._events$.value;
  }
  /** 当前回放索引 */
  get replayIndex() {
    return this._replayIndex$.value;
  }
  /** 是否处于回放模式 */
  get isReplaying() {
    return this._replayIndex$.value >= 0;
  }
  /** 事件存储是否开启 */
  get storeEnabled() {
    return this._storeEnabled$.value;
  }
  /** 事件存储开关状态（响应式） */
  get storeEnabled$() {
    return this._storeEnabled$.asObservable();
  }
  /** 设置事件存储开关 */
  setStoreEnabled(enabled) {
    this._storeEnabled$.next(enabled);
  }
  // ========== 时间旅行 ==========
  /** 跳转到指定时间点 */
  jumpTo(index) {
    const max = this._events$.value.length - 1;
    this._replayIndex$.next(Math.min(Math.max(-1, index), max));
  }
  /** 跳转到开始 */
  jumpToStart() {
    this.jumpTo(0);
  }
  /** 跳转到结束（实时模式） */
  jumpToEnd() {
    this.jumpTo(-1);
  }
  /** 前进一步 */
  stepForward() {
    if (this._replayIndex$.value < 0) return;
    this.jumpTo(this._replayIndex$.value + 1);
  }
  /** 后退一步 */
  stepBackward() {
    const current = this._replayIndex$.value;
    if (current < 0) {
      this.jumpTo(this._events$.value.length - 2);
    } else if (current > 0) {
      this.jumpTo(current - 1);
    }
  }
  // ========== 响应式查询 ==========
  /** 当前可见事件（支持回放切片） */
  get visibleEvents$() {
    return combineLatest([
      this._events$,
      this._replayIndex$
    ]).pipe(map(([events, index]) => index < 0 ? events : events.slice(0, index + 1)));
  }
  /** 获取节点当前状态（响应式） */
  getNodeState$(nodeId) {
    return this.visibleEvents$.pipe(map((events) => {
      for (let i = events.length - 1; i >= 0; i--) {
        const e = events[i];
        if (e.id === nodeId && e.type !== "node_emit") return e;
      }
      return void 0;
    }));
  }
  /** 获取所有节点的当前状态 */
  get nodeStates$() {
    return this.visibleEvents$.pipe(map((events) => {
      const states = /* @__PURE__ */ new Map();
      for (const event of events) {
        if (event.id && event.type !== "node_emit") {
          states.set(event.id, event);
        }
      }
      return states;
    }));
  }
  /** 获取成功节点 ID 集合 */
  get successNodeIds$() {
    return this.visibleEvents$.pipe(map((events) => new Set(events.filter((e) => e.type === "node_success" && e.id).map((e) => e.id))));
  }
  /** 获取失败节点 ID 集合 */
  get failedNodeIds$() {
    return this.visibleEvents$.pipe(map((events) => {
      const success = /* @__PURE__ */ new Set();
      const failed = /* @__PURE__ */ new Set();
      for (const e of events) {
        if (!e.id) continue;
        if (e.type === "node_success") {
          success.add(e.id);
          failed.delete(e.id);
        } else if (e.type === "node_fail" && !success.has(e.id)) {
          failed.add(e.id);
        }
      }
      return failed;
    }));
  }
  // ========== 续跑支持 ==========
  /** 获取成功节点 ID 集合（同步） */
  get successNodeIds() {
    return new Set(this._events$.value.filter((e) => e.type === "node_success" && e.id).map((e) => e.id));
  }
  /** 获取单个节点的所有事件（续跑重放用） */
  getNodeEvents(nodeId) {
    return this._events$.value.filter((e) => e.id === nodeId);
  }
  /** 检查节点是否已成功执行 */
  isNodeSuccess(nodeId) {
    return this._events$.value.some((e) => e.id === nodeId && e.type === "node_success");
  }
  // ========== 回放功能 ==========
  /**
   * 自动回放（调试/演示）
   * @param speed 每个事件的间隔（毫秒）
   */
  autoReplay$(speed = 200) {
    return from(this._events$.value).pipe(concatMap((event, index) => {
      this.jumpTo(index);
      return from([
        event
      ]).pipe(delay(speed));
    }));
  }
  /**
   * 从指定位置开始回放
   */
  replayFrom$(startIndex, speed = 200) {
    const events = this._events$.value.slice(startIndex);
    return from(events).pipe(concatMap((event, index) => {
      this.jumpTo(startIndex + index);
      return from([
        event
      ]).pipe(delay(speed));
    }));
  }
  // ========== 序列化 ==========
  /** 导出为 JSON（持久化） */
  toJSON() {
    return {
      events: this._events$.value,
      replayIndex: this._replayIndex$.value,
      storeEnabled: this._storeEnabled$.value
    };
  }
  /** 从 JSON 恢复 */
  static fromJSON(json) {
    const stream = new _WorkflowEventStream();
    stream._events$.next(json.events || []);
    stream._replayIndex$.next(json.replayIndex ?? -1);
    stream._storeEnabled$.next(json.storeEnabled ?? false);
    return stream;
  }
};

// src/runtime/workflow-runtime.ts
var WorkflowRuntime = class {
  static {
    __name(this, "WorkflowRuntime");
  }
  /** 全局事件流（单例） */
  eventStream = new WorkflowEventStream();
  /**
   * 获取全局事件流
   */
  get events() {
    return this.eventStream;
  }
  /**
   * 开始录制事件
   *
   * 语义：清空历史事件，开启事件存储
   * 用途：工作流执行前调用
   */
  startRecording() {
    this.eventStream.clear();
    this.eventStream.setStoreEnabled(true);
  }
  /**
   * 停止录制事件
   *
   * 语义：关闭事件存储，保留已有事件
   * 用途：工作流执行完成后调用
   */
  stopRecording() {
    this.eventStream.setStoreEnabled(false);
  }
  /**
   * 清空所有事件
   */
  clearEvents() {
    this.eventStream.clear();
  }
};
var globalRuntime = new WorkflowRuntime();
function setGlobalRuntime(runtime) {
  globalRuntime = runtime;
}
__name(setGlobalRuntime, "setGlobalRuntime");

// src/execution/visitor-executor.ts
function _ts_decorate4(decorators, target, key, desc) {
  var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
  else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  return c > 3 && r && Object.defineProperty(target, key, r), r;
}
__name(_ts_decorate4, "_ts_decorate");
function _ts_metadata2(k, v) {
  if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
__name(_ts_metadata2, "_ts_metadata");
var VisitorExecutor = class {
  static {
    __name(this, "VisitorExecutor");
  }
  eventStore;
  defaultVisitor;
  constructor() {
    this.eventStore = root6.get(EVENT_STORE, new MemoryEventStore());
    this.defaultVisitor = root6.get(DEFAULT_VISITOR, new DefaultVisitor());
  }
  visit(ast, input$, parent) {
    if (parent && globalRuntime.events.isNodeSuccess(ast.id)) {
      const cachedEvents = globalRuntime.events.getNodeEvents(ast.id);
      return from2(cachedEvents);
    }
    const type = findNodeType(ast.type);
    const methods = root6.get(HANDLER_METHOD, []);
    if (!methods || methods.length === 0) {
      console.error(`[VisitorExecutor] \u672A\u627E\u5230\u4EFB\u4F55 Handler`);
      return this.handleError(new Error(`\u672A\u627E\u5230\u4EFB\u4F55 Handler`), ast);
    }
    const method = methods.find((it) => it.ast === type);
    if (!method) {
      return this.recordEvents(this.useDefaultVisitor(ast, input$, parent), parent);
    }
    const instance = root6.get(method.target);
    if (!method.property || typeof instance[method.property] !== "function") {
      console.error(`[VisitorExecutor] Handler \u65B9\u6CD5\u4E0D\u5B58\u5728\u6216\u4E0D\u53EF\u8C03\u7528: ${String(method.property)}`);
      return this.handleError(new Error(`Handler \u65B9\u6CD5\u4E0D\u5B58\u5728\u6216\u4E0D\u53EF\u8C03\u7528: ${String(method.property)}`), ast);
    }
    try {
      const handlerFn = instance[method.property];
      const result = handlerFn.call(instance, ast, input$, parent);
      const execute$ = this.normalizeResult(result, ast);
      const recordTarget = this.isWorkflowGraphAst(ast) ? ast : parent;
      return this.recordEvents(execute$, recordTarget);
    } catch (error) {
      console.error(`[VisitorExecutor] \u6267\u884C\u8282\u70B9 ${ast.id} \u65F6\u53D1\u751F\u9519\u8BEF:`, error);
      return this.handleError(error, ast);
    }
  }
  /**
   * 记录事件到 eventStream（续跑核心）
   *
   * 设计哲学：
   * - 透明拦截：Handler 无需感知记录逻辑
   * - 完整记录：保存所有事件到全局 eventStream
   */
  recordEvents(source$, workflow) {
    if (!workflow) {
      return source$;
    }
    return source$.pipe(tap((event) => {
      globalRuntime.events.emit(event);
    }));
  }
  /**
   * 将 Handler 返回值统一为 Observable
   *
   * 优雅设计：
   * - 自动识别 Promise、Observable、同步值
   * - 直接传递所有事件，不做转换
   * - 错误转换为失败节点（Error as Data 模式）
   * - 支持嵌套类型：Promise<Observable<INode>>
   */
  normalizeResult(result, ast) {
    if (result && typeof result.subscribe === "function") {
      return result.pipe(catchError((error) => this.handleError(error, ast)));
    }
    if (result && typeof result.then === "function") {
      return from2(result).pipe(switchMap((res) => {
        if (res && typeof res.subscribe === "function") {
          return this.normalizeResult(res, ast);
        }
        return of(res);
      }), catchError((error) => this.handleError(error, ast)));
    }
    return of(result).pipe(catchError((error) => this.handleError(error, ast)));
  }
  useDefaultVisitor(ast, input$, workflow) {
    return this.defaultVisitor.visit(ast, input$, workflow).pipe(catchError((error) => this.handleError(error, ast)));
  }
  /**
   * 创建失败状态的节点
   *
   * 优雅设计：
   * - 纯函数：不修改原节点，返回新节点
   * - Error as Data：错误作为节点的属性，而非异常
   */
  createFailedNode(ast, error) {
    const failedNode = {
      ...ast
    };
    failedNode.state = "fail";
    setAstError(failedNode, error);
    return {
      type: "node_fail",
      id: ast.id,
      error: ast.error?.message
    };
  }
  /**
   * 统一错误处理
   *
   * 优雅设计：
   * - 设置节点状态为 fail
   * - 发射 node_fail 和 node_emit 事件后完成流（错误不中断运行，下游节点可继续处理）
   */
  handleError(error, ast) {
    return new Observable3((obs) => {
      obs.next(this.createFailedNode(ast, error));
      const outputs = ast.metadata?.outputs || [];
      if (outputs.length > 0) {
        const emitData = {};
        outputs.forEach((output) => {
          emitData[output.property] = null;
        });
        obs.next({
          type: "node_emit",
          id: ast.id,
          data: emitData
        });
      }
      obs.complete();
    });
  }
  /**
   * 检查节点是否为工作流图
   */
  isWorkflowGraphAst(ast) {
    return ast.type === "WorkflowGraphAst" || "nodes" in ast;
  }
};
VisitorExecutor = _ts_decorate4([
  Injectable3(),
  _ts_metadata2("design:type", Function),
  _ts_metadata2("design:paramtypes", [])
], VisitorExecutor);

// src/executor.ts
function _ts_decorate5(decorators, target, key, desc) {
  var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
  else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  return c > 3 && r && Object.defineProperty(target, key, r), r;
}
__name(_ts_decorate5, "_ts_decorate");
function _ts_metadata3(k, v) {
  if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
__name(_ts_metadata3, "_ts_metadata");
function _ts_param(paramIndex, decorator) {
  return function(target, key) {
    decorator(target, key, paramIndex);
  };
}
__name(_ts_param, "_ts_param");
var NodeExecutor = class {
  static {
    __name(this, "NodeExecutor");
  }
  visitorExecutor;
  compiler;
  constructor(visitorExecutor, compiler) {
    this.visitorExecutor = visitorExecutor;
    this.compiler = compiler;
  }
  /**
   * 执行节点
   *
   * @param node 要执行的节点
   * @param input$ 输入流（每次发射触发一次执行）
   * @returns 节点事件流
   */
  run(node, input$, parent) {
    return defer(() => {
      const compiledNode = isNode(node) ? node : this.compiler.compile(node);
      const nodeInstance = this.cloneNode(compiledNode);
      return this.visitorExecutor.visit(nodeInstance, input$, parent);
    });
  }
  /**
   * 克隆节点（stateful 节点跳过克隆以保留累积状态）
   */
  cloneNode(node) {
    if (isWorkflowGraphAst(node)) {
      return node;
    }
    if (node.metadata?.class?.stateful) {
      return node;
    }
    try {
      if (typeof structuredClone !== "undefined") {
        return structuredClone(node);
      }
    } catch {
    }
    return clone(node);
  }
};
NodeExecutor = _ts_decorate5([
  Injectable4(),
  _ts_param(0, Inject(VisitorExecutor)),
  _ts_param(1, Inject(Compiler)),
  _ts_metadata3("design:type", Function),
  _ts_metadata3("design:paramtypes", [
    typeof VisitorExecutor === "undefined" ? Object : VisitorExecutor,
    typeof Compiler === "undefined" ? Object : Compiler
  ])
], NodeExecutor);
function executeAst(node, input, parent) {
  const executor = root7.get(NodeExecutor);
  return executor.run(node, of2(input || {}), parent);
}
__name(executeAst, "executeAst");
function executeWorkflow(workflow, input) {
  const executor = root7.get(NodeExecutor);
  return executor.run(workflow, of2(input || {}));
}
__name(executeWorkflow, "executeWorkflow");
function executeWorkflowImmediate(workflow, input) {
  return new Promise((resolve, reject) => {
    let finalWorkflow = workflow;
    executeWorkflow(workflow, input).subscribe({
      next: /* @__PURE__ */ __name((event) => {
      }, "next"),
      complete: /* @__PURE__ */ __name(() => resolve(finalWorkflow), "complete"),
      error: reject
    });
  });
}
__name(executeWorkflowImmediate, "executeWorkflowImmediate");
function executeAstWithWorkflowGraph(node, input, workflow) {
  const executor = root7.get(NodeExecutor);
  return executor.run(node, of2(input || {}), workflow);
}
__name(executeAstWithWorkflowGraph, "executeAstWithWorkflowGraph");
function executeNodeIsolated(node, input) {
  const executor = root7.get(NodeExecutor);
  return executor.run(node, of2(input || {}));
}
__name(executeNodeIsolated, "executeNodeIsolated");

// src/errors.ts
import { NoRetryError } from "@sker/core";

// src/TextAreaAst.ts
import { Injectable as Injectable5 } from "@sker/core";
import { isObservable as isObservable3, Observable as Observable4 } from "rxjs";
function _ts_decorate6(decorators, target, key, desc) {
  var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
  else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  return c > 3 && r && Object.defineProperty(target, key, r), r;
}
__name(_ts_decorate6, "_ts_decorate");
function _ts_metadata4(k, v) {
  if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
__name(_ts_metadata4, "_ts_metadata");
var serializeToString = /* @__PURE__ */ __name((value) => {
  if (value === null) return "null";
  if (value === void 0) return "undefined";
  const type = typeof value;
  if (type === "string") return value;
  if (type === "number") return isNaN(value) ? "NaN" : String(value);
  if (type === "boolean") return String(value);
  if (Array.isArray(value)) {
    return value.flat().map((item) => serializeToString(item)).join("\n\n");
  }
  if (type === "object") {
    try {
      return JSON.stringify(value, null, 2);
    } catch {
      return String(value);
    }
  }
  return String(value);
}, "serializeToString");
var TextAreaAst = class extends Ast {
  static {
    __name(this, "TextAreaAst");
  }
  input = [];
  output = "";
  type = `TextAreaAst`;
};
_ts_decorate6([
  Input({
    title: "\u8F93\u5165",
    type: "textarea",
    mode: IS_MULTI,
    defaultValue: []
  }),
  _ts_metadata4("design:type", Array)
], TextAreaAst.prototype, "input", void 0);
_ts_decorate6([
  Output({
    title: "\u8F93\u51FA",
    type: "textarea",
    defaultValue: ""
  }),
  _ts_metadata4("design:type", String)
], TextAreaAst.prototype, "output", void 0);
TextAreaAst = _ts_decorate6([
  Node({
    title: "\u6587\u672C\u8282\u70B9",
    type: "basic"
  })
], TextAreaAst);
var TextAreaAstVisitor = class {
  static {
    __name(this, "TextAreaAstVisitor");
  }
  handler(ast, input$, ctx) {
    if (!input$) throw new Error(`[TextAreaAstVisitor.handler] input$ is empty`);
    if (!isObservable3(input$)) throw new Error(`[TextAreaAstVisitor.handler] input$ must be an Observable`);
    return new Observable4((obs) => {
      ast.state = "running";
      obs.next({
        type: "node_runing",
        id: ast.id
      });
      input$.subscribe({
        next: /* @__PURE__ */ __name((input) => {
          ast.emitCount += 1;
          const output = serializeToString(input.input);
          ast.output = output;
          obs.next({
            type: "node_emit",
            id: ast.id,
            data: {
              emitCount: ast.emitCount,
              output
            }
          });
        }, "next"),
        error: /* @__PURE__ */ __name((error) => {
          ast.state = "fail";
          setAstError(ast, error);
          obs.next({
            type: "node_fail",
            id: ast.id,
            error: ast.error?.message
          });
        }, "error"),
        complete: /* @__PURE__ */ __name(() => {
          ast.state = "success";
          obs.next({
            type: "node_success",
            id: ast.id
          });
          obs.complete();
        }, "complete")
      });
    });
  }
};
_ts_decorate6([
  Handler(TextAreaAst),
  _ts_metadata4("design:type", Function),
  _ts_metadata4("design:paramtypes", [
    typeof TextAreaAst === "undefined" ? Object : TextAreaAst,
    typeof Observable4 === "undefined" ? Object : Observable4,
    typeof WorkflowGraphAst === "undefined" ? Object : WorkflowGraphAst
  ]),
  _ts_metadata4("design:returntype", typeof Observable4 === "undefined" ? Object : Observable4)
], TextAreaAstVisitor.prototype, "handler", null);
TextAreaAstVisitor = _ts_decorate6([
  Injectable5()
], TextAreaAstVisitor);
var TextAreaAstTool = class {
  static {
    __name(this, "TextAreaAstTool");
  }
  handler(ast) {
    return {
      id: ast.id,
      title: ast.name || "\u672A\u547D\u540D\u6587\u672C",
      summary: ast.description || "",
      content: ast.output || "\uFF08\u5185\u5BB9\u4E3A\u7A7A\uFF09",
      emitCount: ast.emitCount || 0
    };
  }
};
_ts_decorate6([
  Tool(TextAreaAst),
  _ts_metadata4("design:type", Function),
  _ts_metadata4("design:paramtypes", [
    typeof TextAreaAst === "undefined" ? Object : TextAreaAst
  ]),
  _ts_metadata4("design:returntype", void 0)
], TextAreaAstTool.prototype, "handler", null);
TextAreaAstTool = _ts_decorate6([
  Injectable5()
], TextAreaAstTool);

// src/MarkdownAst.ts
import { Injectable as Injectable6 } from "@sker/core";
import { isObservable as isObservable4, Observable as Observable5 } from "rxjs";
function _ts_decorate7(decorators, target, key, desc) {
  var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
  else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  return c > 3 && r && Object.defineProperty(target, key, r), r;
}
__name(_ts_decorate7, "_ts_decorate");
function _ts_metadata5(k, v) {
  if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
__name(_ts_metadata5, "_ts_metadata");
var MarkdownAst = class extends Ast {
  static {
    __name(this, "MarkdownAst");
  }
  input = [];
  output = "";
  type = `MarkdownAst`;
};
_ts_decorate7([
  Input({
    title: "\u8F93\u5165",
    type: "richtext",
    mode: IS_MULTI,
    defaultValue: []
  }),
  _ts_metadata5("design:type", Array)
], MarkdownAst.prototype, "input", void 0);
_ts_decorate7([
  Output({
    title: "\u8F93\u51FA",
    type: "richtext",
    defaultValue: ""
  }),
  _ts_metadata5("design:type", String)
], MarkdownAst.prototype, "output", void 0);
MarkdownAst = _ts_decorate7([
  Node({
    title: "Markdown\u8282\u70B9",
    type: "basic"
  })
], MarkdownAst);
var MarkdownAstVisitor = class {
  static {
    __name(this, "MarkdownAstVisitor");
  }
  handler(ast, input$, ctx) {
    if (!input$) throw new Error(`[MarkdownAstVisitor.handler] input$ is empty`);
    if (!isObservable4(input$)) throw new Error(`[MarkdownAstVisitor.handler] input$ must be an Observable`);
    return new Observable5((obs) => {
      ast.state = "running";
      obs.next({
        type: "node_runing",
        id: ast.id
      });
      input$.subscribe({
        next: /* @__PURE__ */ __name((input) => {
          ast.emitCount += 1;
          const output = serializeToString(input.input);
          ast.output = output;
          obs.next({
            type: "node_emit",
            id: ast.id,
            data: {
              emitCount: ast.emitCount,
              output
            }
          });
        }, "next"),
        error: /* @__PURE__ */ __name((error) => {
          ast.state = "fail";
          setAstError(ast, error);
          obs.next({
            type: "node_fail",
            id: ast.id,
            error: ast.error?.message
          });
        }, "error"),
        complete: /* @__PURE__ */ __name(() => {
          ast.state = "success";
          obs.next({
            type: "node_success",
            id: ast.id
          });
          obs.complete();
        }, "complete")
      });
    });
  }
};
_ts_decorate7([
  Handler(MarkdownAst),
  _ts_metadata5("design:type", Function),
  _ts_metadata5("design:paramtypes", [
    typeof MarkdownAst === "undefined" ? Object : MarkdownAst,
    typeof Observable5 === "undefined" ? Object : Observable5,
    typeof WorkflowGraphAst === "undefined" ? Object : WorkflowGraphAst
  ]),
  _ts_metadata5("design:returntype", typeof Observable5 === "undefined" ? Object : Observable5)
], MarkdownAstVisitor.prototype, "handler", null);
MarkdownAstVisitor = _ts_decorate7([
  Injectable6()
], MarkdownAstVisitor);
var MarkdownAstTool = class {
  static {
    __name(this, "MarkdownAstTool");
  }
  handler(ast) {
    return {
      id: ast.id,
      title: ast.name || "\u672A\u547D\u540DMarkdown",
      summary: ast.description || "",
      content: ast.output || "\uFF08\u5185\u5BB9\u4E3A\u7A7A\uFF09",
      emitCount: ast.emitCount || 0
    };
  }
};
_ts_decorate7([
  Tool(MarkdownAst),
  _ts_metadata5("design:type", Function),
  _ts_metadata5("design:paramtypes", [
    typeof MarkdownAst === "undefined" ? Object : MarkdownAst
  ]),
  _ts_metadata5("design:returntype", void 0)
], MarkdownAstTool.prototype, "handler", null);
MarkdownAstTool = _ts_decorate7([
  Injectable6()
], MarkdownAstTool);

// src/DateAst.ts
import dayjs from "dayjs";
function _ts_decorate8(decorators, target, key, desc) {
  var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
  else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  return c > 3 && r && Object.defineProperty(target, key, r), r;
}
__name(_ts_decorate8, "_ts_decorate");
function _ts_metadata6(k, v) {
  if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
__name(_ts_metadata6, "_ts_metadata");
var DateAst = class extends Ast {
  static {
    __name(this, "DateAst");
  }
  dateStr = dayjs().format("YYYY-MM-DD HH:mm:ss");
  date = /* @__PURE__ */ new Date();
  type = `DateAst`;
};
_ts_decorate8([
  Input({
    title: "\u65E5\u671F",
    type: "datetime-local"
  }),
  _ts_metadata6("design:type", String)
], DateAst.prototype, "dateStr", void 0);
_ts_decorate8([
  Output({
    title: "\u65E5\u671F",
    type: "datetime-local"
  }),
  _ts_metadata6("design:type", typeof Date === "undefined" ? Object : Date)
], DateAst.prototype, "date", void 0);
DateAst = _ts_decorate8([
  Node({
    title: "\u65E5\u671F",
    type: "basic"
  })
], DateAst);

// src/MqAst.ts
function _ts_decorate9(decorators, target, key, desc) {
  var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
  else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  return c > 3 && r && Object.defineProperty(target, key, r), r;
}
__name(_ts_decorate9, "_ts_decorate");
function _ts_metadata7(k, v) {
  if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
__name(_ts_metadata7, "_ts_metadata");
var MqPushAst = class extends Ast {
  static {
    __name(this, "MqPushAst");
  }
  type = `MqPushAst`;
  queueName = ``;
  input = ``;
  success = false;
};
_ts_decorate9([
  Input({
    title: "\u961F\u5217\u540D\u79F0"
  }),
  _ts_metadata7("design:type", String)
], MqPushAst.prototype, "queueName", void 0);
_ts_decorate9([
  Input({
    title: "\u6D88\u606F\u5185\u5BB9"
  }),
  _ts_metadata7("design:type", Object)
], MqPushAst.prototype, "input", void 0);
_ts_decorate9([
  Output({
    title: "\u63A8\u9001\u7ED3\u679C"
  }),
  _ts_metadata7("design:type", Boolean)
], MqPushAst.prototype, "success", void 0);
MqPushAst = _ts_decorate9([
  Node({
    title: "\u63A8\u9001\u6D88\u606F",
    type: "basic"
  })
], MqPushAst);
var MqPullAst = class extends Ast {
  static {
    __name(this, "MqPullAst");
  }
  type = `MqPullAst`;
  queueName = ``;
  max = 10;
  output = ``;
};
_ts_decorate9([
  Input({
    title: "\u961F\u5217\u540D\u79F0"
  }),
  _ts_metadata7("design:type", String)
], MqPullAst.prototype, "queueName", void 0);
_ts_decorate9([
  Input({
    title: "\u6700\u591A\u62C9\u53D6\u6B21\u6570",
    defaultValue: 10
  }),
  _ts_metadata7("design:type", Number)
], MqPullAst.prototype, "max", void 0);
_ts_decorate9([
  Output({
    title: "\u6D88\u606F\u5185\u5BB9"
  }),
  _ts_metadata7("design:type", String)
], MqPullAst.prototype, "output", void 0);
MqPullAst = _ts_decorate9([
  Node({
    title: "\u62C9\u53D6\u6D88\u606F",
    type: "basic"
  })
], MqPullAst);

// src/StoreAst.ts
function _ts_decorate10(decorators, target, key, desc) {
  var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
  else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  return c > 3 && r && Object.defineProperty(target, key, r), r;
}
__name(_ts_decorate10, "_ts_decorate");
function _ts_metadata8(k, v) {
  if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
__name(_ts_metadata8, "_ts_metadata");
var StoreGetAst = class extends Ast {
  static {
    __name(this, "StoreGetAst");
  }
  type = `StoreGetAst`;
  key = ``;
  value = ``;
};
_ts_decorate10([
  Input({
    title: "\u952E\u540D"
  }),
  _ts_metadata8("design:type", String)
], StoreGetAst.prototype, "key", void 0);
_ts_decorate10([
  Output({
    title: "\u503C"
  }),
  _ts_metadata8("design:type", Object)
], StoreGetAst.prototype, "value", void 0);
StoreGetAst = _ts_decorate10([
  Node({
    title: "\u83B7\u53D6\u5B58\u50A8",
    type: "basic"
  })
], StoreGetAst);
var StoreSetAst = class extends Ast {
  static {
    __name(this, "StoreSetAst");
  }
  type = `StoreSetAst`;
  key = ``;
  value = ``;
};
_ts_decorate10([
  Input({
    title: "\u952E\u540D"
  }),
  _ts_metadata8("design:type", String)
], StoreSetAst.prototype, "key", void 0);
_ts_decorate10([
  Input({
    title: "\u503C"
  }),
  Output({
    title: "\u503C"
  }),
  _ts_metadata8("design:type", Object)
], StoreSetAst.prototype, "value", void 0);
StoreSetAst = _ts_decorate10([
  Node({
    title: "\u8BBE\u7F6E\u5B58\u50A8",
    type: "basic"
  })
], StoreSetAst);

// src/CollectorAst.ts
import { Injectable as Injectable7 } from "@sker/core";
import { Observable as Observable6, map as map2, tap as tap2, toArray } from "rxjs";
function _ts_decorate11(decorators, target, key, desc) {
  var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
  else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  return c > 3 && r && Object.defineProperty(target, key, r), r;
}
__name(_ts_decorate11, "_ts_decorate");
function _ts_metadata9(k, v) {
  if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
__name(_ts_metadata9, "_ts_metadata");
var CollectorAst = class extends Ast {
  static {
    __name(this, "CollectorAst");
  }
  items = [];
  result = [];
  type = "CollectorAst";
};
_ts_decorate11([
  Input({
    title: "\u6570\u636E\u6D41",
    mode: IS_BUFFER | IS_MULTI
  }),
  _ts_metadata9("design:type", Array)
], CollectorAst.prototype, "items", void 0);
_ts_decorate11([
  Output({
    title: "\u6536\u96C6\u7ED3\u679C"
  }),
  _ts_metadata9("design:type", Array)
], CollectorAst.prototype, "result", void 0);
CollectorAst = _ts_decorate11([
  Node({
    title: "\u6536\u96C6\u5668",
    type: "basic"
  })
], CollectorAst);
var CollectorVisitor = class {
  static {
    __name(this, "CollectorVisitor");
  }
  handler(ast, input$, ctx) {
    return new Observable6((obs) => {
      ast.state = "running";
      obs.next({
        type: "node_runing",
        id: ast.id
      });
      input$.pipe(toArray(), map2((asts) => asts.flatMap((a) => a.items || []).flat()), tap2((items) => {
        ast.result = items;
        obs.next({
          type: "node_emit",
          id: ast.id,
          data: {
            result: items
          }
        });
        ast.state = "success";
        obs.next({
          type: "node_success",
          id: ast.id
        });
      })).subscribe({
        complete: /* @__PURE__ */ __name(() => obs.complete(), "complete")
      });
    });
  }
};
_ts_decorate11([
  Handler(CollectorAst),
  _ts_metadata9("design:type", Function),
  _ts_metadata9("design:paramtypes", [
    typeof CollectorAst === "undefined" ? Object : CollectorAst,
    typeof Observable6 === "undefined" ? Object : Observable6,
    typeof WorkflowGraphAst === "undefined" ? Object : WorkflowGraphAst
  ]),
  _ts_metadata9("design:returntype", void 0)
], CollectorVisitor.prototype, "handler", null);
CollectorVisitor = _ts_decorate11([
  Injectable7()
], CollectorVisitor);

// src/LoopAst.ts
function _ts_decorate12(decorators, target, key, desc) {
  var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
  else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  return c > 3 && r && Object.defineProperty(target, key, r), r;
}
__name(_ts_decorate12, "_ts_decorate");
function _ts_metadata10(k, v) {
  if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
__name(_ts_metadata10, "_ts_metadata");
var LoopAst = class extends Ast {
  static {
    __name(this, "LoopAst");
  }
  items = [];
  batchSize = 1;
  delay = 0;
  current = void 0;
  index = 0;
  total = 0;
  done = false;
  type = "LoopAst";
};
_ts_decorate12([
  Input({
    title: "\u6570\u636E",
    mode: IS_MULTI,
    type: "any"
  }),
  _ts_metadata10("design:type", Array)
], LoopAst.prototype, "items", void 0);
_ts_decorate12([
  State({
    title: "\u6279\u91CF\u5927\u5C0F",
    type: "number"
  }),
  _ts_metadata10("design:type", Number)
], LoopAst.prototype, "batchSize", void 0);
_ts_decorate12([
  State({
    title: "\u5EF6\u8FDF(ms)",
    type: "number"
  }),
  _ts_metadata10("design:type", Number)
], LoopAst.prototype, "delay", void 0);
_ts_decorate12([
  Output({
    title: "\u5F53\u524D\u9879"
  }),
  _ts_metadata10("design:type", Object)
], LoopAst.prototype, "current", void 0);
_ts_decorate12([
  Output({
    title: "\u5F53\u524D\u7D22\u5F15"
  }),
  _ts_metadata10("design:type", Number)
], LoopAst.prototype, "index", void 0);
_ts_decorate12([
  Output({
    title: "\u603B\u6570"
  }),
  _ts_metadata10("design:type", Number)
], LoopAst.prototype, "total", void 0);
_ts_decorate12([
  Output({
    title: "\u5B8C\u6210\u4FE1\u53F7"
  }),
  _ts_metadata10("design:type", Boolean)
], LoopAst.prototype, "done", void 0);
LoopAst = _ts_decorate12([
  Node({
    title: "\u5FAA\u73AF",
    type: "control"
  })
], LoopAst);

// src/FilterAst.ts
import { Injectable as Injectable8 } from "@sker/core";
import { Observable as Observable7 } from "rxjs";
function _ts_decorate13(decorators, target, key, desc) {
  var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
  else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  return c > 3 && r && Object.defineProperty(target, key, r), r;
}
__name(_ts_decorate13, "_ts_decorate");
function _ts_metadata11(k, v) {
  if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
__name(_ts_metadata11, "_ts_metadata");
var FilterAst = class extends Ast {
  static {
    __name(this, "FilterAst");
  }
  items = [];
  conditions = [];
  logic = "and";
  expression;
  matched = [];
  matchedCount = 0;
  type = "FilterAst";
};
_ts_decorate13([
  Input({
    title: "\u6570\u636E",
    mode: IS_MULTI,
    type: "any"
  }),
  _ts_metadata11("design:type", Array)
], FilterAst.prototype, "items", void 0);
_ts_decorate13([
  State({
    title: "\u8FC7\u6EE4\u6761\u4EF6"
  }),
  _ts_metadata11("design:type", Array)
], FilterAst.prototype, "conditions", void 0);
_ts_decorate13([
  State({
    title: "\u6761\u4EF6\u903B\u8F91",
    type: "string"
  }),
  _ts_metadata11("design:type", String)
], FilterAst.prototype, "logic", void 0);
_ts_decorate13([
  State({
    title: "\u8868\u8FBE\u5F0F",
    type: "text"
  }),
  _ts_metadata11("design:type", String)
], FilterAst.prototype, "expression", void 0);
_ts_decorate13([
  Output({
    title: "\u5339\u914D\u6570\u636E"
  }),
  _ts_metadata11("design:type", Array)
], FilterAst.prototype, "matched", void 0);
_ts_decorate13([
  Output({
    title: "\u5339\u914D\u6570\u91CF"
  }),
  _ts_metadata11("design:type", Number)
], FilterAst.prototype, "matchedCount", void 0);
FilterAst = _ts_decorate13([
  Node({
    title: "\u8FC7\u6EE4",
    type: "basic"
  })
], FilterAst);
var FilterAstVisitor = class {
  static {
    __name(this, "FilterAstVisitor");
  }
  handler(ast, workflow) {
    return new Observable7((obs) => {
      ast.state = "running";
      obs.next({
        type: "node_runing",
        id: ast.id
      });
      let items = ast.items || [];
      if (!Array.isArray(items)) {
        items = [
          items
        ];
      }
      items = items.flat();
      let matched;
      if (ast.expression) {
        matched = this.filterByExpression(items, ast.expression);
      } else if (ast.conditions.length > 0) {
        matched = this.filterByConditions(items, ast.conditions, ast.logic);
      } else {
        matched = items.filter(Boolean);
      }
      obs.next({
        type: "node_emit",
        id: ast.id,
        data: {
          matched,
          matchedCount: matched.length
        }
      });
      ast.state = "success";
      obs.next({
        type: "node_success",
        id: ast.id
      });
      obs.complete();
    });
  }
  filterByExpression(items, expression) {
    try {
      const filterFn = new Function("item", "index", `return ${expression}`);
      return items.filter((item, index) => {
        try {
          return filterFn(item, index);
        } catch {
          return false;
        }
      });
    } catch {
      return [];
    }
  }
  filterByConditions(items, conditions, logic) {
    return items.filter((item) => {
      const results = conditions.map((cond) => this.evaluateCondition(item, cond));
      return logic === "and" ? results.every(Boolean) : results.some(Boolean);
    });
  }
  evaluateCondition(item, condition) {
    const value = this.getFieldValue(item, condition.field);
    const target = condition.value;
    const operators = {
      equals: /* @__PURE__ */ __name(() => value === target, "equals"),
      notEquals: /* @__PURE__ */ __name(() => value !== target, "notEquals"),
      contains: /* @__PURE__ */ __name(() => String(value).includes(String(target)), "contains"),
      notContains: /* @__PURE__ */ __name(() => !String(value).includes(String(target)), "notContains"),
      startsWith: /* @__PURE__ */ __name(() => String(value).startsWith(String(target)), "startsWith"),
      endsWith: /* @__PURE__ */ __name(() => String(value).endsWith(String(target)), "endsWith"),
      gt: /* @__PURE__ */ __name(() => Number(value) > Number(target), "gt"),
      gte: /* @__PURE__ */ __name(() => Number(value) >= Number(target), "gte"),
      lt: /* @__PURE__ */ __name(() => Number(value) < Number(target), "lt"),
      lte: /* @__PURE__ */ __name(() => Number(value) <= Number(target), "lte"),
      isEmpty: /* @__PURE__ */ __name(() => value == null || value === "" || Array.isArray(value) && value.length === 0, "isEmpty"),
      isNotEmpty: /* @__PURE__ */ __name(() => value != null && value !== "" && !(Array.isArray(value) && value.length === 0), "isNotEmpty"),
      regex: /* @__PURE__ */ __name(() => new RegExp(String(target)).test(String(value)), "regex"),
      expression: /* @__PURE__ */ __name(() => {
        try {
          return new Function("value", "item", `return ${target}`)(value, item);
        } catch {
          return false;
        }
      }, "expression")
    };
    return operators[condition.operator]?.() ?? false;
  }
  getFieldValue(item, field) {
    if (!field) return item;
    return field.split(".").reduce((obj, key) => obj?.[key], item);
  }
};
_ts_decorate13([
  Handler(FilterAst),
  _ts_metadata11("design:type", Function),
  _ts_metadata11("design:paramtypes", [
    typeof FilterAst === "undefined" ? Object : FilterAst,
    typeof WorkflowGraphAst === "undefined" ? Object : WorkflowGraphAst
  ]),
  _ts_metadata11("design:returntype", typeof Observable7 === "undefined" ? Object : Observable7)
], FilterAstVisitor.prototype, "handler", null);
FilterAstVisitor = _ts_decorate13([
  Injectable8()
], FilterAstVisitor);

// src/MergeAst.ts
function _ts_decorate14(decorators, target, key, desc) {
  var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
  else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  return c > 3 && r && Object.defineProperty(target, key, r), r;
}
__name(_ts_decorate14, "_ts_decorate");
function _ts_metadata12(k, v) {
  if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
__name(_ts_metadata12, "_ts_metadata");
var MergeAst = class extends Ast {
  static {
    __name(this, "MergeAst");
  }
  inputs = [];
  mode = "append";
  result = [];
  totalCount = 0;
  type = "MergeAst";
};
_ts_decorate14([
  Input({
    title: "\u6570\u636E\u6D41",
    mode: IS_MULTI | IS_BUFFER,
    type: "any"
  }),
  _ts_metadata12("design:type", Array)
], MergeAst.prototype, "inputs", void 0);
_ts_decorate14([
  State({
    title: "\u5408\u5E76\u6A21\u5F0F",
    type: "string"
  }),
  _ts_metadata12("design:type", typeof MergeMode === "undefined" ? Object : MergeMode)
], MergeAst.prototype, "mode", void 0);
_ts_decorate14([
  Output({
    title: "\u5408\u5E76\u7ED3\u679C"
  }),
  _ts_metadata12("design:type", Array)
], MergeAst.prototype, "result", void 0);
_ts_decorate14([
  Output({
    title: "\u6570\u636E\u603B\u91CF"
  }),
  _ts_metadata12("design:type", Number)
], MergeAst.prototype, "totalCount", void 0);
MergeAst = _ts_decorate14([
  Node({
    title: "\u5408\u5E76",
    type: "basic"
  })
], MergeAst);

// src/SwitchAst.ts
function _ts_decorate15(decorators, target, key, desc) {
  var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
  else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  return c > 3 && r && Object.defineProperty(target, key, r), r;
}
__name(_ts_decorate15, "_ts_decorate");
function _ts_metadata13(k, v) {
  if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
__name(_ts_metadata13, "_ts_metadata");
var SwitchAst = class extends Ast {
  static {
    __name(this, "SwitchAst");
  }
  value = void 0;
  output_default = ``;
  type = "SwitchAst";
};
_ts_decorate15([
  Input({
    title: "\u8F93\u5165\u503C",
    defaultValue: void 0
  }),
  _ts_metadata13("design:type", Object)
], SwitchAst.prototype, "value", void 0);
_ts_decorate15([
  Output({
    title: "Default",
    condition: "true",
    defaultValue: ""
  })
], SwitchAst.prototype, "output_default", void 0);
SwitchAst = _ts_decorate15([
  Node({
    title: "\u5206\u652F\u8DEF\u7531\u5668",
    type: "control",
    dynamicOutputs: true
  })
], SwitchAst);

// src/SwitchAstVisitor.ts
import { Injectable as Injectable9 } from "@sker/core";
import { Observable as Observable8, from as from3 } from "rxjs";
import { concatMap as concatMap2, mergeMap } from "rxjs/operators";
function _ts_decorate16(decorators, target, key, desc) {
  var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
  else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  return c > 3 && r && Object.defineProperty(target, key, r), r;
}
__name(_ts_decorate16, "_ts_decorate");
function _ts_metadata14(k, v) {
  if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
__name(_ts_metadata14, "_ts_metadata");
var SwitchAstVisitor = class {
  static {
    __name(this, "SwitchAstVisitor");
  }
  handler(ast, input$, ctx) {
    return new Observable8((obs) => {
      const abortController = new AbortController();
      ast.state = "running";
      obs.next({
        type: "node_runing",
        id: ast.id
      });
      const subscription = input$.pipe(concatMap2(async (inputData) => {
        ast.emitCount += 1;
        obs.next({
          type: "node_emit",
          id: ast.id,
          data: {
            emitCount: ast.emitCount
          }
        });
        if (inputData) {
          Object.keys(inputData).forEach((key) => {
            ast[key] = inputData[key];
          });
        }
        if (abortController.signal.aborted) {
          throw new Error("\u5DE5\u4F5C\u6D41\u5DF2\u53D6\u6D88");
        }
        const inputValue = ast.value;
        const outputs = ast.metadata.outputs;
        const defaultOutput = outputs.find((o) => o.condition && (o.condition === "true" || o.property === "output_default"));
        const normalOutputs = outputs.filter((o) => o.condition && o.condition !== "true" && o.property !== "output_default");
        let anyMatched = false;
        const events = [];
        normalOutputs.forEach((outputMeta) => {
          const propKey = String(outputMeta.property);
          const matched = this.evaluateCondition(outputMeta.condition, {
            $input: inputValue
          });
          if (matched) {
            anyMatched = true;
            events.push({
              type: "node_emit",
              id: ast.id,
              data: {
                [propKey]: true
              }
            });
          }
        });
        if (defaultOutput && !anyMatched) {
          const propKey = String(defaultOutput.property);
          events.push({
            type: "node_emit",
            id: ast.id,
            data: {
              [propKey]: true
            }
          });
        }
        return events;
      }), mergeMap((events) => from3(events))).subscribe({
        next: /* @__PURE__ */ __name((event) => obs.next(event), "next"),
        error: /* @__PURE__ */ __name((error) => {
          ast.state = "fail";
          setAstError(ast, error);
          obs.next({
            type: "node_fail",
            id: ast.id,
            error: ast.error?.message
          });
        }, "error"),
        complete: /* @__PURE__ */ __name(() => {
          ast.state = "success";
          obs.next({
            type: "node_success",
            id: ast.id
          });
          obs.complete();
        }, "complete")
      });
      return () => {
        subscription.unsubscribe();
        abortController.abort();
        obs.complete();
      };
    });
  }
  evaluateCondition(condition, context) {
    try {
      const func = new Function(...Object.keys(context), `return ${condition}`);
      return func(...Object.values(context));
    } catch {
      return false;
    }
  }
};
_ts_decorate16([
  Handler(SwitchAst),
  _ts_metadata14("design:type", Function),
  _ts_metadata14("design:paramtypes", [
    typeof SwitchAst === "undefined" ? Object : SwitchAst,
    typeof Observable8 === "undefined" ? Object : Observable8,
    Object
  ]),
  _ts_metadata14("design:returntype", void 0)
], SwitchAstVisitor.prototype, "handler", null);
SwitchAstVisitor = _ts_decorate16([
  Injectable9()
], SwitchAstVisitor);

// src/NotAst.ts
import { Injectable as Injectable10 } from "@sker/core";
import { Observable as Observable9 } from "rxjs";
function _ts_decorate17(decorators, target, key, desc) {
  var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
  else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  return c > 3 && r && Object.defineProperty(target, key, r), r;
}
__name(_ts_decorate17, "_ts_decorate");
function _ts_metadata15(k, v) {
  if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
__name(_ts_metadata15, "_ts_metadata");
var NotAst = class extends Ast {
  static {
    __name(this, "NotAst");
  }
  input = false;
  output = false;
  type = "NotAst";
};
_ts_decorate17([
  Input({
    title: "\u8F93\u5165",
    defaultValue: false
  }),
  _ts_metadata15("design:type", Boolean)
], NotAst.prototype, "input", void 0);
_ts_decorate17([
  Output({
    title: "\u8F93\u51FA",
    defaultValue: false
  }),
  _ts_metadata15("design:type", Boolean)
], NotAst.prototype, "output", void 0);
NotAst = _ts_decorate17([
  Node({
    title: "\u5E03\u5C14\u53D6\u53CD",
    type: "basic",
    errorStrategy: "fail"
  })
], NotAst);
var NotAstVisitor = class {
  static {
    __name(this, "NotAstVisitor");
  }
  handler(ast, input$) {
    return new Observable9((obs) => {
      ast.state = "running";
      obs.next({
        type: "node_runing",
        id: ast.id
      });
      const subscription = input$.subscribe({
        next: /* @__PURE__ */ __name((inputData) => {
          ast.emitCount += 1;
          ast.output = !inputData.input;
          obs.next({
            type: "node_emit",
            id: ast.id,
            data: {
              output: ast.output,
              emitCount: ast.emitCount
            }
          });
        }, "next"),
        error: /* @__PURE__ */ __name((error) => {
          ast.state = "fail";
          setAstError(ast, error);
          obs.next({
            type: "node_fail",
            id: ast.id,
            error: ast.error?.message
          });
        }, "error"),
        complete: /* @__PURE__ */ __name(() => {
          ast.state = "success";
          obs.next({
            type: "node_success",
            id: ast.id
          });
          obs.complete();
        }, "complete")
      });
      return () => {
        subscription.unsubscribe();
        obs.complete();
      };
    });
  }
};
_ts_decorate17([
  Handler(NotAst),
  _ts_metadata15("design:type", Function),
  _ts_metadata15("design:paramtypes", [
    typeof NotAst === "undefined" ? Object : NotAst,
    typeof Observable9 === "undefined" ? Object : Observable9
  ]),
  _ts_metadata15("design:returntype", typeof Observable9 === "undefined" ? Object : Observable9)
], NotAstVisitor.prototype, "handler", null);
NotAstVisitor = _ts_decorate17([
  Injectable10()
], NotAstVisitor);

// src/BooleanAst.ts
import { Injectable as Injectable11 } from "@sker/core";
import { concatMap as concatMap3, from as from4, mergeMap as mergeMap2, Observable as Observable10 } from "rxjs";
function _ts_decorate18(decorators, target, key, desc) {
  var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
  else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  return c > 3 && r && Object.defineProperty(target, key, r), r;
}
__name(_ts_decorate18, "_ts_decorate");
function _ts_metadata16(k, v) {
  if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
__name(_ts_metadata16, "_ts_metadata");
var BooleanAst = class extends Ast {
  static {
    __name(this, "BooleanAst");
  }
  value = null;
  result;
  type = "BooleanAst";
};
_ts_decorate18([
  Input({
    title: "\u8F93\u5165\u503C",
    defaultValue: null
  }),
  _ts_metadata16("design:type", Object)
], BooleanAst.prototype, "value", void 0);
_ts_decorate18([
  Output({
    title: "\u5E03\u5C14\u503C",
    defaultValue: false
  }),
  _ts_metadata16("design:type", Boolean)
], BooleanAst.prototype, "result", void 0);
BooleanAst = _ts_decorate18([
  Node({
    title: "\u5E03\u5C14\u8F6C\u6362",
    type: "basic",
    errorStrategy: "skip"
  })
], BooleanAst);
var BooleanAstVisitor = class {
  static {
    __name(this, "BooleanAstVisitor");
  }
  visit(ast, input$, ctx) {
    return new Observable10((obs) => {
      ast.state = "running";
      obs.next({
        type: "node_runing",
        id: ast.id
      });
      const subscription = input$.pipe(concatMap3(async (inputData) => {
        if (inputData?.value !== void 0) {
          ast.value = inputData.value;
        }
        const result = this.toBoolean(ast.value);
        ast.result = result;
        ast.emitCount += 1;
        return [
          {
            type: "node_emit",
            id: ast.id,
            data: {
              result: ast.result,
              emitCount: ast.emitCount
            }
          }
        ];
      }), mergeMap2((events) => from4(events))).subscribe({
        next: /* @__PURE__ */ __name((event) => obs.next(event), "next"),
        error: /* @__PURE__ */ __name((error) => {
          ast.state = "fail";
          obs.next({
            type: "node_fail",
            id: ast.id,
            error: error.message
          });
        }, "error"),
        complete: /* @__PURE__ */ __name(() => {
          ast.state = "success";
          obs.next({
            type: "node_success",
            id: ast.id
          });
          obs.complete();
        }, "complete")
      });
      return () => {
        subscription.unsubscribe();
        obs.complete();
      };
    });
  }
  toBoolean(value) {
    if (value === null || value === void 0) return false;
    if (typeof value === "boolean") return value;
    if (typeof value === "number") return value !== 0;
    if (typeof value === "string") return value.length > 0;
    if (Array.isArray(value)) return value.length > 0;
    if (typeof value === "object") return Object.keys(value).length > 0;
    return false;
  }
};
_ts_decorate18([
  Handler(BooleanAst),
  _ts_metadata16("design:type", Function),
  _ts_metadata16("design:paramtypes", [
    typeof BooleanAst === "undefined" ? Object : BooleanAst,
    typeof Observable10 === "undefined" ? Object : Observable10,
    Object
  ]),
  _ts_metadata16("design:returntype", typeof Observable10 === "undefined" ? Object : Observable10)
], BooleanAstVisitor.prototype, "visit", null);
BooleanAstVisitor = _ts_decorate18([
  Injectable11()
], BooleanAstVisitor);

// src/WorkflowGraphAstVisitor.ts
import { Injectable as Injectable18, Inject as Inject2 } from "@sker/core";
import { Observable as Observable13, EMPTY as EMPTY3, of as of3, isObservable as isObservable5, combineLatest as combineLatest3 } from "rxjs";
import { map as map5, catchError as catchError2, shareReplay, concatMap as concatMap4, withLatestFrom as withLatestFrom2, startWith } from "rxjs/operators";

// src/execution/NodeInputBuilder.ts
import { Injectable as Injectable12 } from "@sker/core";
function _ts_decorate19(decorators, target, key, desc) {
  var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
  else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  return c > 3 && r && Object.defineProperty(target, key, r), r;
}
__name(_ts_decorate19, "_ts_decorate");
var NodeInputBuilder = class {
  static {
    __name(this, "NodeInputBuilder");
  }
  /**
   * 识别入口节点（入度为 0 的节点）
   */
  findEntryNodes(workflow) {
    const inDegrees = /* @__PURE__ */ new Map();
    workflow.nodes.forEach((node) => inDegrees.set(node.id, 0));
    workflow.edges.forEach((edge) => {
      inDegrees.set(edge.to, (inDegrees.get(edge.to) ?? 0) + 1);
    });
    return Array.from(inDegrees.entries()).filter(([_, degree]) => degree === 0).map(([nodeId]) => nodeId);
  }
  /**
   * 按目标节点分组边
   */
  groupEdgesByTarget(edges) {
    const edgesByTarget = /* @__PURE__ */ new Map();
    edges.forEach((edge) => {
      if (!edgesByTarget.has(edge.to)) {
        edgesByTarget.set(edge.to, []);
      }
      edgesByTarget.get(edge.to).push(edge);
    });
    return edgesByTarget;
  }
  /**
   * 分离普通边和 router 边
   */
  separateEdgesByType(edges, workflow) {
    const normalEdges = [];
    const routerEdges = [];
    edges.forEach((edge) => {
      const sourceNode = workflow.nodes.find((n) => n.id === edge.from);
      const outputMeta = sourceNode?.metadata?.outputs?.find((out) => out.property === edge.fromProperty);
      if (outputMeta?.isRouter) {
        routerEdges.push(edge);
      } else {
        normalEdges.push(edge);
      }
    });
    return {
      normalEdges,
      routerEdges
    };
  }
  /**
   * 构建节点输入对象
   *
   * 优先级：
   * 1. input[nodeId.property] - 精确匹配
   * 2. input[nodeId][property] - 嵌套对象
   * 3. node[property] - 节点自身属性
   * 4. inputMeta.defaultValue - 默认值
   */
  buildNodeInput(node, input) {
    const nodeInput = {};
    const inputs = node.metadata?.inputs || [];
    inputs.forEach((inputMeta) => {
      const property = String(inputMeta.property);
      const propertyKey = `${node.id}.${property}`;
      if (input[propertyKey] !== void 0) {
        nodeInput[property] = input[propertyKey];
      } else if (input[node.id]?.[property] !== void 0) {
        nodeInput[property] = input[node.id][property];
      } else if (node[property] !== void 0) {
        nodeInput[property] = node[property];
      } else if (inputMeta.defaultValue !== void 0) {
        nodeInput[property] = inputMeta.defaultValue;
      }
    });
    return nodeInput;
  }
  /**
   * 验证端口的边数量是否符合 isMulti 标记
   *
   * 规则：如果一个端口有多条边连接，必须标记 mode: IS_MULTI
   */
  validatePortEdges(node, edges) {
    const inputs = node.metadata?.inputs || [];
    const edgesByPort = /* @__PURE__ */ new Map();
    edges.forEach((edge) => {
      if (!edge.toProperty) return;
      if (!edgesByPort.has(edge.toProperty)) {
        edgesByPort.set(edge.toProperty, []);
      }
      edgesByPort.get(edge.toProperty).push(edge);
    });
    edgesByPort.forEach((portEdges, portName) => {
      if (portEdges.length <= 1) return;
      const inputMeta = inputs.find((i) => i.property === portName);
      const isMulti = hasMultiMode(inputMeta?.mode);
      if (!isMulti) {
        throw new Error(`[NodeInputBuilder] \u8282\u70B9 ${node.id} \u7684\u7AEF\u53E3 "${portName}" \u6709 ${portEdges.length} \u6761\u8FB9\uFF0C\u4F46\u672A\u6807\u8BB0 mode: IS_MULTI`);
      }
    });
  }
};
NodeInputBuilder = _ts_decorate19([
  Injectable12()
], NodeInputBuilder);

// src/execution/EdgeStreamBuilder.ts
import { EMPTY, defer as defer2 } from "rxjs";
import { filter, map as map3, toArray as toArray2 } from "rxjs/operators";

// src/edge-transform.ts
function evaluateTransform(input, expression) {
  try {
    const fn = new Function("$input", `return ${expression}`);
    return fn(input);
  } catch (error) {
    console.error("[EdgeTransform] \u8868\u8FBE\u5F0F\u6267\u884C\u5931\u8D25:", expression, error);
    throw new Error(`\u8FB9\u8F6C\u6362\u5931\u8D25: ${error instanceof Error ? error.message : String(error)}`);
  }
}
__name(evaluateTransform, "evaluateTransform");

// src/execution/EdgeStreamBuilder.ts
import { Injectable as Injectable13 } from "@sker/core";
function _ts_decorate20(decorators, target, key, desc) {
  var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
  else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  return c > 3 && r && Object.defineProperty(target, key, r), r;
}
__name(_ts_decorate20, "_ts_decorate");
var EdgeStreamBuilder = class {
  static {
    __name(this, "EdgeStreamBuilder");
  }
  /**
   * 从边构建值流
   *
   * @param edge 边定义
   * @param nodeEventStreams 所有节点的事件流映射
   * @param targetNode 目标节点，用于检查 IS_BUFFER 模式
   * @returns 边的值流
   */
  buildEdgeValueStream(edge, nodeEventStreams, targetNode) {
    return defer2(() => {
      const eventStream$ = nodeEventStreams.get(edge.from);
      if (!eventStream$) {
        return EMPTY;
      }
      let valueStream$ = eventStream$.pipe(filter((event) => event.type === "node_emit" && edge.fromProperty in (event.data || {})), map3((event) => this.extractAndTransformValue(event, edge)));
      if (targetNode) {
        const inputMeta = targetNode.metadata?.inputs?.find((input) => input.property === edge.toProperty);
        if (hasBufferMode(inputMeta?.mode)) {
          valueStream$ = valueStream$.pipe(toArray2());
        }
      }
      return valueStream$;
    });
  }
  /**
   * 提取并转换边的值
   */
  extractAndTransformValue(event, edge) {
    let value = event.data?.[edge.fromProperty];
    if (edge.transform) {
      value = evaluateTransform(value, edge.transform);
    }
    return value;
  }
  /**
   * 过滤掉 ROUTE_SKIPPED 的值
   */
  filterRouteSkipped(stream$) {
    return stream$.pipe(filter((value) => value !== ROUTE_SKIPPED));
  }
};
EdgeStreamBuilder = _ts_decorate20([
  Injectable13()
], EdgeStreamBuilder);

// src/execution/EdgeCombiner.ts
import { Injectable as Injectable14 } from "@sker/core";
function _ts_decorate21(decorators, target, key, desc) {
  var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
  else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  return c > 3 && r && Object.defineProperty(target, key, r), r;
}
__name(_ts_decorate21, "_ts_decorate");
var EdgeCombiner = class {
  static {
    __name(this, "EdgeCombiner");
  }
  /**
   * 按 EdgeMode 分组边
   */
  groupEdgesByMode(edges) {
    const groups = /* @__PURE__ */ new Map();
    edges.forEach((edge) => {
      const mode = edge.mode ?? EdgeMode.COMBINE_LATEST;
      if (!groups.has(mode)) {
        groups.set(mode, []);
      }
      groups.get(mode).push(edge);
    });
    return groups;
  }
};
EdgeCombiner = _ts_decorate21([
  Injectable14()
], EdgeCombiner);

// src/execution/StreamMerger.ts
import { Injectable as Injectable15 } from "@sker/core";
import { Observable as Observable11, EMPTY as EMPTY2 } from "rxjs";
function _ts_decorate22(decorators, target, key, desc) {
  var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
  else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  return c > 3 && r && Object.defineProperty(target, key, r), r;
}
__name(_ts_decorate22, "_ts_decorate");
var StreamMerger = class {
  static {
    __name(this, "StreamMerger");
  }
  /**
   * 合并多个输入流，只有当所有流都 complete 时才 complete
   *
   * 与普通 merge 的区别：
   * - merge: 任一流 complete 不影响其他流，但整体 complete 时机不确定
   * - mergeWithCompletion: 合并所有值，只有当所有流都 complete 时才 complete
   *
   * 核心设计：nodeInput$ = mergeWithCompletion(input$, router$, ...)
   * - 多个输入源通过 merge 合并值
   * - 只有当所有输入源都 complete 时，节点输入流才 complete
   * - 这确保了循环场景下，入口节点不会过早结束
   */
  mergeWithCompletion(sources) {
    if (sources.length === 0) return EMPTY2;
    if (sources.length === 1) return sources[0];
    return new Observable11((subscriber) => {
      let completedCount = 0;
      const total = sources.length;
      const subscriptions = sources.map((source, index) => source.subscribe({
        next: /* @__PURE__ */ __name((value) => {
          subscriber.next(value);
        }, "next"),
        error: /* @__PURE__ */ __name((err) => {
          subscriber.error(err);
        }, "error"),
        complete: /* @__PURE__ */ __name(() => {
          completedCount++;
          if (completedCount === total) {
            subscriber.complete();
          }
        }, "complete")
      }));
      return () => subscriptions.forEach((sub) => sub.unsubscribe());
    });
  }
};
StreamMerger = _ts_decorate22([
  Injectable15()
], StreamMerger);

// src/execution/WorkflowEventMerger.ts
import { Observable as Observable12 } from "rxjs";
import { Injectable as Injectable16 } from "@sker/core";
function _ts_decorate23(decorators, target, key, desc) {
  var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
  else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  return c > 3 && r && Object.defineProperty(target, key, r), r;
}
__name(_ts_decorate23, "_ts_decorate");
var WorkflowEventMerger = class {
  static {
    __name(this, "WorkflowEventMerger");
  }
  /**
   * 合并所有节点事件流，管理工作流状态
   *
   * 逻辑：
   * 1. 发射 node_runing 事件
   * 2. 订阅所有节点事件流
   * 3. 转发所有节点事件
   * 4. 记录节点成功/失败状态
   * 5. 当所有节点完成时，决定工作流最终状态
   */
  mergeNodeEventStreams(workflow, nodeEventStreams) {
    return new Observable12((obs) => {
      obs.next({
        type: "node_runing",
        id: workflow.id
      });
      const allStreams = Array.from(nodeEventStreams.values());
      if (allStreams.length === 0) {
        workflow.state = "success";
        obs.next({
          type: "node_success",
          id: workflow.id
        });
        obs.complete();
        return;
      }
      let completedCount = 0;
      const totalNodes = allStreams.length;
      const nodeStates = /* @__PURE__ */ new Map();
      const subscriptions = [];
      allStreams.forEach((nodeStream) => {
        const sub = nodeStream.subscribe({
          next: /* @__PURE__ */ __name((event) => {
            obs.next(event);
            if (event.type === "node_fail") {
              nodeStates.set(event.id, "fail");
              if (event.error && !workflow.error) {
                setAstError(workflow, new Error(event.error));
              }
            } else if (event.type === "node_success") {
              nodeStates.set(event.id, "success");
            }
          }, "next"),
          error: /* @__PURE__ */ __name((err) => {
            nodeStates.set(workflow.id, "fail");
            workflow.state = "fail";
            workflow.error = err;
            setAstError(workflow, err);
            obs.next({
              type: "node_fail",
              id: workflow.id,
              error: workflow.error?.message
            });
          }, "error"),
          complete: /* @__PURE__ */ __name(() => {
            completedCount++;
            if (completedCount === totalNodes) {
              const hasError = Array.from(nodeStates.values()).some((s) => s === "fail");
              workflow.state = hasError ? "fail" : "success";
              if (hasError) {
                const error = workflow.error || new Error("Workflow failed");
                obs.next({
                  type: "node_fail",
                  id: workflow.id,
                  error: error.message
                });
                obs.complete();
              } else {
                obs.next({
                  type: "node_success",
                  id: workflow.id
                });
                obs.complete();
              }
            }
          }, "complete")
        });
        subscriptions.push(sub);
      });
      return () => subscriptions.forEach((sub) => sub.unsubscribe());
    });
  }
};
WorkflowEventMerger = _ts_decorate23([
  Injectable16()
], WorkflowEventMerger);

// src/execution/EdgeModeStrategy.ts
import { merge, zip, combineLatest as combineLatest2 } from "rxjs";
import { map as map4, withLatestFrom } from "rxjs/operators";
import { Injectable as Injectable17, MapInjectionToken } from "@sker/core";
function _ts_decorate24(decorators, target, key, desc) {
  var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
  else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  return c > 3 && r && Object.defineProperty(target, key, r), r;
}
__name(_ts_decorate24, "_ts_decorate");
var EDGE_MODE_STRATEGY = new MapInjectionToken(`EDGE_MODE_STRATEGY`, {
  factory: /* @__PURE__ */ __name(() => {
    const map6 = /* @__PURE__ */ new Map();
    return map6;
  }, "factory")
});
var MergeStrategy = class {
  static {
    __name(this, "MergeStrategy");
  }
  combine(sources, edges) {
    return merge(...sources.map((source, sourceIndex) => source.pipe(map4((value) => ({
      [edges[sourceIndex].toProperty]: value
    })))));
  }
};
MergeStrategy = _ts_decorate24([
  Injectable17()
], MergeStrategy);
var ZipStrategy = class {
  static {
    __name(this, "ZipStrategy");
  }
  combine(sources, edges) {
    return zip(...sources).pipe(map4((values) => this.mapToObject(values, edges)));
  }
  mapToObject(values, edges) {
    const result = {};
    values.forEach((value, index) => {
      const prop = edges[index]?.toProperty;
      if (prop) {
        if (result[prop] === void 0) {
          result[prop] = value;
        } else if (Array.isArray(result[prop])) {
          result[prop].push(value);
        } else {
          result[prop] = [
            result[prop],
            value
          ];
        }
      }
    });
    return result;
  }
};
ZipStrategy = _ts_decorate24([
  Injectable17()
], ZipStrategy);
var CombineLatestStrategy = class {
  static {
    __name(this, "CombineLatestStrategy");
  }
  combine(sources, edges) {
    return combineLatest2(sources).pipe(map4((values) => this.mapToObject(values, edges)));
  }
  mapToObject(values, edges) {
    const result = {};
    values.forEach((value, index) => {
      const prop = edges[index]?.toProperty;
      if (prop) result[prop] = value;
    });
    return result;
  }
};
CombineLatestStrategy = _ts_decorate24([
  Injectable17()
], CombineLatestStrategy);
var WithLatestFromStrategy = class {
  static {
    __name(this, "WithLatestFromStrategy");
  }
  combine(sources, edges) {
    const primaryIndex = edges.findIndex((edge) => edge.isPrimary === true);
    if (primaryIndex === -1) {
      console.warn("[WithLatestFromStrategy] \u672A\u627E\u5230\u4E3B\u6D41\uFF08isPrimary=true\uFF09\uFF0C\u56DE\u9000\u5230 combineLatest");
      return this.fallbackToCombineLatest(sources, edges);
    }
    const primarySource = sources[primaryIndex];
    const secondarySources = sources.filter((_, i) => i !== primaryIndex);
    const primaryEdge = edges[primaryIndex];
    const secondaryEdges = edges.filter((_, i) => i !== primaryIndex);
    if (secondarySources.length === 0) {
      return primarySource.pipe(map4((value) => ({
        [primaryEdge.toProperty]: value
      })));
    }
    return primarySource.pipe(withLatestFrom(...secondarySources), map4(([primaryValue, ...secondaryValues]) => {
      const result = {};
      if (primaryEdge.toProperty) {
        result[primaryEdge.toProperty] = primaryValue;
      }
      secondaryValues.forEach((value, index) => {
        const prop = secondaryEdges[index]?.toProperty;
        if (prop) result[prop] = value;
      });
      return result;
    }));
  }
  /**
   * 回退到 combineLatest（当未找到主流时）
   */
  fallbackToCombineLatest(sources, edges) {
    return combineLatest2(sources).pipe(map4((values) => {
      const result = {};
      values.forEach((value, index) => {
        const prop = edges[index]?.toProperty;
        if (prop) result[prop] = value;
      });
      return result;
    }));
  }
};
WithLatestFromStrategy = _ts_decorate24([
  Injectable17()
], WithLatestFromStrategy);
var providers = [
  {
    provide: EDGE_MODE_STRATEGY,
    useClass: MergeStrategy,
    multi: "map",
    mapKey: EdgeMode.MERGE
  },
  {
    provide: EDGE_MODE_STRATEGY,
    useClass: ZipStrategy,
    multi: "map",
    mapKey: EdgeMode.ZIP
  },
  {
    provide: EDGE_MODE_STRATEGY,
    useClass: CombineLatestStrategy,
    multi: "map",
    mapKey: EdgeMode.COMBINE_LATEST
  },
  {
    provide: EDGE_MODE_STRATEGY,
    useClass: WithLatestFromStrategy,
    multi: "map",
    mapKey: EdgeMode.WITH_LATEST_FROM
  }
];

// src/WorkflowGraphAstVisitor.ts
function _ts_decorate25(decorators, target, key, desc) {
  var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
  else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  return c > 3 && r && Object.defineProperty(target, key, r), r;
}
__name(_ts_decorate25, "_ts_decorate");
function _ts_metadata17(k, v) {
  if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
__name(_ts_metadata17, "_ts_metadata");
function _ts_param2(paramIndex, decorator) {
  return function(target, key) {
    decorator(target, key, paramIndex);
  };
}
__name(_ts_param2, "_ts_param");
var WorkflowGraphAstVisitor = class {
  static {
    __name(this, "WorkflowGraphAstVisitor");
  }
  nodeExecutor;
  nodeInputBuilder;
  edgeStreamBuilder;
  edgeCombiner;
  streamMerger;
  workflowEventMerger;
  strategies;
  constructor(nodeExecutor, nodeInputBuilder, edgeStreamBuilder, edgeCombiner, streamMerger, workflowEventMerger, strategies) {
    this.nodeExecutor = nodeExecutor;
    this.nodeInputBuilder = nodeInputBuilder;
    this.edgeStreamBuilder = edgeStreamBuilder;
    this.edgeCombiner = edgeCombiner;
    this.streamMerger = streamMerger;
    this.workflowEventMerger = workflowEventMerger;
    this.strategies = strategies;
  }
  handler(ast, input$, _parent) {
    if (!input$) throw new Error(`[WorkflowGraphAstVisitor.handler] input$ is empty`);
    if (!isObservable5(input$)) throw new Error(`[WorkflowGraphAstVisitor.handler] input$ must be an Observable`);
    return input$.pipe(concatMap4((input) => {
      ast.state = "running";
      ast.error = void 0;
      const nodeEventStreams = /* @__PURE__ */ new Map();
      const nodeInputStreams = this.buildNodeInputStreams(ast, of3(input), nodeEventStreams);
      ast.nodes.forEach((node, index) => {
        const nodeInput$ = nodeInputStreams.get(node.id) || EMPTY3;
        const eventStream$ = this.nodeExecutor.run(node, nodeInput$, ast).pipe(shareReplay({
          bufferSize: Infinity,
          refCount: false
        }));
        nodeEventStreams.set(node.id, eventStream$);
      });
      return this.workflowEventMerger.mergeNodeEventStreams(ast, nodeEventStreams);
    }), catchError2((error) => {
      ast.state = "fail";
      ast.error = error;
      return of3({
        type: "node_fail",
        id: ast.id,
        error: error.message
      }, {
        type: "node_emit",
        id: ast.id,
        data: {}
      });
    }));
  }
  /**
   * 构建每个节点的输入流
   *
   * 核心设计：nodeInput$ = mergeWithCompletion(input$, router$, ...)
   * - 多个输入源通过 merge 合并值
   * - 只有当所有输入源都 complete 时，节点输入流才 complete
   * - 这确保了循环场景下，入口节点不会过早结束
   */
  buildNodeInputStreams(workflow, systemInput$, nodeEventStreams) {
    const nodeInputStreams = /* @__PURE__ */ new Map();
    const entryIds = workflow.entryNodeIds?.length ? workflow.entryNodeIds : this.nodeInputBuilder.findEntryNodes(workflow);
    const edgesByTarget = this.nodeInputBuilder.groupEdgesByTarget(workflow.edges);
    workflow.nodes.forEach((node) => {
      const edges = edgesByTarget.get(node.id) || [];
      const isEntry = entryIds.includes(node.id);
      const inputSources = [];
      if (isEntry) {
        const entryInput$ = systemInput$.pipe(map5((input) => this.nodeInputBuilder.buildNodeInput(node, input)));
        inputSources.push(entryInput$);
      }
      const { normalEdges, routerEdges } = this.nodeInputBuilder.separateEdgesByType(edges, workflow);
      if (normalEdges.length > 0) {
        const normalInput$ = this.buildNormalEdgesInput(workflow, normalEdges, nodeEventStreams, node);
        if (normalInput$ !== EMPTY3) {
          inputSources.push(normalInput$);
        }
      }
      routerEdges.forEach((edge) => {
        const routerInput$ = this.buildRouterEdgeInput(workflow, edge, nodeEventStreams, node.id, edges, node);
        if (routerInput$ !== EMPTY3) {
          inputSources.push(routerInput$);
        }
      });
      if (inputSources.length === 0) {
        const staticInput = this.nodeInputBuilder.buildNodeInput(node, {});
        nodeInputStreams.set(node.id, of3(staticInput));
      } else if (inputSources.length === 1) {
        nodeInputStreams.set(node.id, inputSources[0]);
      } else {
        nodeInputStreams.set(node.id, this.streamMerger.mergeWithCompletion(inputSources));
      }
    });
    return nodeInputStreams;
  }
  /**
   * 构建普通边的输入流（按 EdgeMode 组合）
   */
  buildNormalEdgesInput(workflow, edges, nodeEventStreams, targetNode) {
    if (edges.length === 0) return EMPTY3;
    this.nodeInputBuilder.validatePortEdges(targetNode, edges);
    const modeGroups = this.edgeCombiner.groupEdgesByMode(edges);
    const groupStreams = [];
    modeGroups.forEach((edgesInMode, mode) => {
      const stream$ = this.combineEdgesByMode(workflow, mode, edgesInMode, nodeEventStreams, targetNode);
      if (stream$ !== EMPTY3) {
        groupStreams.push({
          mode,
          priority: EDGE_MODE_PRIORITY[mode],
          stream$
        });
      }
    });
    if (groupStreams.length === 0) return EMPTY3;
    if (groupStreams.length === 1) return groupStreams[0].stream$;
    groupStreams.sort((a, b) => a.priority - b.priority);
    return combineLatest3(groupStreams.map((g) => g.stream$)).pipe(map5((results) => Object.assign({}, ...results)));
  }
  /**
   * 构建 router 边的输入流（独立触发）
   *
   * Router 语义：
   * - 作为主触发源，每次发射都重新触发目标节点
   * - 携带其他输入边的最新值，保持节点的完整输入上下文
   * - 类似 withLatestFrom 的主流，但router边可以有多个
   */
  buildRouterEdgeInput(workflow, edge, nodeEventStreams, targetNodeId, allEdgesToTarget, targetNode) {
    const valueStream$ = this.edgeStreamBuilder.buildEdgeValueStream(edge, nodeEventStreams, targetNode);
    if (valueStream$ === EMPTY3) return EMPTY3;
    const filteredStream$ = this.edgeStreamBuilder.filterRouteSkipped(valueStream$);
    const otherEdges = this.getOtherNonRouterEdges(workflow, edge, allEdgesToTarget);
    if (otherEdges.length === 0) {
      return filteredStream$.pipe(map5((value) => ({
        [edge.toProperty]: value
      })));
    }
    return this.buildRouterWithOtherEdges(filteredStream$, edge, otherEdges, nodeEventStreams, targetNode);
  }
  /**
   * 获取其他非 router 边
   */
  getOtherNonRouterEdges(workflow, currentEdge, allEdges) {
    return allEdges.filter((e) => {
      if (e.id === currentEdge.id) return false;
      const sourceNode = workflow.nodes.find((n) => n.id === e.from);
      const outputMeta = sourceNode?.metadata?.outputs?.find((out) => out.property === e.fromProperty);
      return !outputMeta?.isRouter;
    });
  }
  /**
   * 构建 router 边携带其他边的值流
   */
  buildRouterWithOtherEdges(routerStream$, routerEdge, otherEdges, nodeEventStreams, targetNode) {
    const otherValueStreams = otherEdges.map((e) => this.edgeStreamBuilder.buildEdgeValueStream(e, nodeEventStreams, targetNode)).filter((s) => s !== EMPTY3).map((s) => s.pipe(startWith(void 0)));
    if (otherValueStreams.length === 0) {
      return routerStream$.pipe(map5((value) => ({
        [routerEdge.toProperty]: value
      })));
    }
    return routerStream$.pipe(withLatestFrom2(...otherValueStreams), map5(([routerValue, ...otherValues]) => {
      const result = {};
      result[routerEdge.toProperty] = routerValue;
      otherValues.forEach((value, index) => {
        const otherEdge = otherEdges[index];
        if (otherEdge?.toProperty && value !== void 0) {
          result[otherEdge.toProperty] = value;
        }
      });
      return result;
    }));
  }
  /**
   * 按 EdgeMode 组合边的值流
   */
  combineEdgesByMode(workflow, mode, edges, nodeEventStreams, targetNode) {
    const sources = edges.map((edge) => this.edgeStreamBuilder.buildEdgeValueStream(edge, nodeEventStreams, targetNode)).filter((s) => s !== EMPTY3);
    if (sources.length === 0) return EMPTY3;
    const strategy = this.strategies.get(mode) || this.strategies.get(EdgeMode.COMBINE_LATEST);
    return strategy.combine(sources, edges);
  }
};
_ts_decorate25([
  Handler(WorkflowGraphAst),
  _ts_metadata17("design:type", Function),
  _ts_metadata17("design:paramtypes", [
    typeof WorkflowGraphAst === "undefined" ? Object : WorkflowGraphAst,
    typeof Observable13 === "undefined" ? Object : Observable13,
    typeof WorkflowGraphAst === "undefined" ? Object : WorkflowGraphAst
  ]),
  _ts_metadata17("design:returntype", typeof Observable13 === "undefined" ? Object : Observable13)
], WorkflowGraphAstVisitor.prototype, "handler", null);
WorkflowGraphAstVisitor = _ts_decorate25([
  Injectable18(),
  _ts_param2(0, Inject2(NodeExecutor)),
  _ts_param2(1, Inject2(NodeInputBuilder)),
  _ts_param2(2, Inject2(EdgeStreamBuilder)),
  _ts_param2(3, Inject2(EdgeCombiner)),
  _ts_param2(4, Inject2(StreamMerger)),
  _ts_param2(5, Inject2(WorkflowEventMerger)),
  _ts_param2(6, Inject2(EDGE_MODE_STRATEGY)),
  _ts_metadata17("design:type", Function),
  _ts_metadata17("design:paramtypes", [
    typeof NodeExecutor === "undefined" ? Object : NodeExecutor,
    typeof NodeInputBuilder === "undefined" ? Object : NodeInputBuilder,
    typeof EdgeStreamBuilder === "undefined" ? Object : EdgeStreamBuilder,
    typeof EdgeCombiner === "undefined" ? Object : EdgeCombiner,
    typeof StreamMerger === "undefined" ? Object : StreamMerger,
    typeof WorkflowEventMerger === "undefined" ? Object : WorkflowEventMerger,
    typeof Map === "undefined" ? Object : Map
  ])
], WorkflowGraphAstVisitor);

// src/PassThroughAst.ts
import { Injectable as Injectable19 } from "@sker/core";
import { Observable as Observable14 } from "rxjs";
function _ts_decorate26(decorators, target, key, desc) {
  var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
  else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  return c > 3 && r && Object.defineProperty(target, key, r), r;
}
__name(_ts_decorate26, "_ts_decorate");
function _ts_metadata18(k, v) {
  if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
__name(_ts_metadata18, "_ts_metadata");
var PassThroughAst = class extends Ast {
  static {
    __name(this, "PassThroughAst");
  }
  input = [];
  enable = [];
  mode = "some";
  threshold = 0.5;
  output = "";
  type = "PassThroughAst";
};
_ts_decorate26([
  Input({
    title: "\u8F93\u5165",
    defaultValue: [],
    mode: IS_MULTI
  }),
  _ts_metadata18("design:type", Array)
], PassThroughAst.prototype, "input", void 0);
_ts_decorate26([
  Input({
    title: "\u542F\u7528\u6761\u4EF6",
    defaultValue: [],
    mode: IS_MULTI
  }),
  _ts_metadata18("design:type", Array)
], PassThroughAst.prototype, "enable", void 0);
_ts_decorate26([
  Input({
    title: "\u6761\u4EF6\u6A21\u5F0F",
    defaultValue: "every",
    type: "select",
    options: [
      "every",
      "some",
      "none",
      "majority",
      "count",
      "xor",
      "ratio",
      "first",
      "sequential",
      "weighted"
    ]
  }),
  _ts_metadata18("design:type", String)
], PassThroughAst.prototype, "mode", void 0);
_ts_decorate26([
  Input({
    title: "\u9608\u503C",
    defaultValue: 0.5
  }),
  _ts_metadata18("design:type", Number)
], PassThroughAst.prototype, "threshold", void 0);
_ts_decorate26([
  Output({
    title: "\u8F93\u51FA",
    defaultValue: "",
    isRouter: true
  }),
  _ts_metadata18("design:type", Object)
], PassThroughAst.prototype, "output", void 0);
PassThroughAst = _ts_decorate26([
  Node({
    title: "\u900F\u4F20\u8282\u70B9",
    type: "basic"
  })
], PassThroughAst);
var PassThroughAstVisitor = class {
  static {
    __name(this, "PassThroughAstVisitor");
  }
  /**
  * 检查数组中是否存在连续 n 个 true
  */
  hasSequentialTrue(conditions, n) {
    if (n <= 0 || n > conditions.length) return false;
    let consecutiveCount = 0;
    for (const condition of conditions) {
      if (condition === true) {
        consecutiveCount++;
        if (consecutiveCount >= n) {
          return true;
        }
      } else {
        consecutiveCount = 0;
      }
    }
    return false;
  }
  /**
  * 从工作流图中获取连接到指定节点端口的边权重
  */
  getEdgeWeights(ast, ctx, portName) {
    if (!ctx || !ctx.edges) return [];
    const enableEdges = ctx.edges.filter((edge) => edge.to === ast.id && edge.toProperty === portName);
    return enableEdges.map((edge) => edge.weight ?? 1);
  }
  visit(ast, input$, ctx) {
    return new Observable14((obs) => {
      ast.state = "running";
      obs.next({
        type: "node_runing",
        id: ast.id
      });
      const subscription = input$.subscribe({
        next: /* @__PURE__ */ __name((inputData) => {
          const enableConditions = Array.isArray(inputData.enable) ? inputData.enable : [
            inputData.enable
          ];
          const mode = inputData.mode || "some";
          const threshold = inputData.threshold ?? 0.5;
          let shouldPass = false;
          if (enableConditions.length === 0) {
            shouldPass = true;
          } else {
            const trueCount = enableConditions.filter((condition) => condition === true).length;
            const totalCount = enableConditions.length;
            switch (mode) {
              case "every":
                shouldPass = trueCount === totalCount;
                break;
              case "some":
                shouldPass = trueCount > 0;
                break;
              case "none":
                shouldPass = trueCount === 0;
                break;
              case "majority":
                shouldPass = trueCount > totalCount / 2;
                break;
              case "count":
                shouldPass = trueCount >= threshold;
                break;
              case "xor":
                shouldPass = trueCount === 1;
                break;
              case "ratio":
                shouldPass = trueCount / totalCount >= threshold;
                break;
              case "first":
                shouldPass = enableConditions[0] === true;
                break;
              case "sequential":
                shouldPass = this.hasSequentialTrue(enableConditions, Math.floor(threshold));
                break;
              case "weighted":
                const weights = this.getEdgeWeights(ast, ctx, "enable");
                if (weights.length === 0 || weights.length !== enableConditions.length) {
                  shouldPass = trueCount === totalCount;
                } else {
                  const totalWeight = weights.reduce((sum, w) => sum + w, 0);
                  const actualWeight = enableConditions.reduce((sum, condition, i) => {
                    return sum + (condition ? weights[i] : 0);
                  }, 0);
                  const weightedScore = totalWeight > 0 ? actualWeight / totalWeight : 0;
                  shouldPass = weightedScore >= threshold;
                }
                break;
              default:
                shouldPass = true;
            }
          }
          if (shouldPass) {
            ast.emitCount += 1;
            if (Array.isArray(inputData.input) && inputData.input.length === 1) {
              ast.output = inputData.input[0];
            } else {
              ast.output = inputData.input;
            }
            obs.next({
              type: "node_emit",
              id: ast.id,
              data: {
                output: ast.output,
                emitCount: ast.emitCount
              }
            });
          }
        }, "next"),
        error: /* @__PURE__ */ __name((error) => {
          ast.state = "fail";
          setAstError(ast, error);
          obs.next({
            type: "node_fail",
            id: ast.id,
            error: ast.error?.message
          });
        }, "error"),
        complete: /* @__PURE__ */ __name(() => {
          ast.state = "success";
          obs.next({
            type: "node_success",
            id: ast.id
          });
          obs.complete();
        }, "complete")
      });
      return () => {
        subscription.unsubscribe();
        obs.complete();
      };
    });
  }
};
_ts_decorate26([
  Handler(PassThroughAst),
  _ts_metadata18("design:type", Function),
  _ts_metadata18("design:paramtypes", [
    typeof PassThroughAst === "undefined" ? Object : PassThroughAst,
    typeof Observable14 === "undefined" ? Object : Observable14,
    typeof WorkflowGraphAst === "undefined" ? Object : WorkflowGraphAst
  ]),
  _ts_metadata18("design:returntype", typeof Observable14 === "undefined" ? Object : Observable14)
], PassThroughAstVisitor.prototype, "visit", null);
PassThroughAstVisitor = _ts_decorate26([
  Injectable19()
], PassThroughAstVisitor);

// src/ImageAst.ts
function _ts_decorate27(decorators, target, key, desc) {
  var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
  else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  return c > 3 && r && Object.defineProperty(target, key, r), r;
}
__name(_ts_decorate27, "_ts_decorate");
function _ts_metadata19(k, v) {
  if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
__name(_ts_metadata19, "_ts_metadata");
var ImageAst = class extends Ast {
  static {
    __name(this, "ImageAst");
  }
  uploadedImage = "";
  image = ``;
  type = "ImageAst";
};
_ts_decorate27([
  Input({
    title: "\u8F93\u5165",
    type: "image",
    defaultValue: ""
  }),
  _ts_metadata19("design:type", String)
], ImageAst.prototype, "uploadedImage", void 0);
_ts_decorate27([
  Output({
    title: "\u8F93\u51FA",
    defaultValue: ""
  })
], ImageAst.prototype, "image", void 0);
ImageAst = _ts_decorate27([
  Node({
    title: "\u56FE\u7247",
    type: "basic"
  })
], ImageAst);

// src/AudioAst.ts
function _ts_decorate28(decorators, target, key, desc) {
  var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
  else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  return c > 3 && r && Object.defineProperty(target, key, r), r;
}
__name(_ts_decorate28, "_ts_decorate");
function _ts_metadata20(k, v) {
  if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
__name(_ts_metadata20, "_ts_metadata");
var AudioAst = class extends Ast {
  static {
    __name(this, "AudioAst");
  }
  uploadedAudio = "";
  audio = ``;
  type = "AudioAst";
};
_ts_decorate28([
  Input({
    title: "\u97F3\u9891",
    type: "audio",
    defaultValue: ""
  }),
  _ts_metadata20("design:type", String)
], AudioAst.prototype, "uploadedAudio", void 0);
_ts_decorate28([
  Output({
    title: "\u97F3\u9891",
    defaultValue: ""
  })
], AudioAst.prototype, "audio", void 0);
AudioAst = _ts_decorate28([
  Node({
    title: "\u97F3\u9891",
    type: "basic"
  })
], AudioAst);

// src/VideoAst.ts
function _ts_decorate29(decorators, target, key, desc) {
  var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
  else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  return c > 3 && r && Object.defineProperty(target, key, r), r;
}
__name(_ts_decorate29, "_ts_decorate");
function _ts_metadata21(k, v) {
  if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
__name(_ts_metadata21, "_ts_metadata");
var VideoAst = class extends Ast {
  static {
    __name(this, "VideoAst");
  }
  uploadedVideo = "";
  // 注意：不使用BehaviorSubject
  video = ``;
  type = "VideoAst";
};
_ts_decorate29([
  Input({
    title: "\u89C6\u9891",
    type: "video",
    defaultValue: ""
  }),
  _ts_metadata21("design:type", String)
], VideoAst.prototype, "uploadedVideo", void 0);
_ts_decorate29([
  Output({
    title: "\u89C6\u9891",
    defaultValue: ""
  })
], VideoAst.prototype, "video", void 0);
VideoAst = _ts_decorate29([
  Node({
    title: "\u89C6\u9891",
    type: "basic"
  })
], VideoAst);

// src/schedulers/crawler-scheduler.ts
import { Subscription, asyncScheduler } from "rxjs";
var ConcurrencyScheduler = class {
  static {
    __name(this, "ConcurrencyScheduler");
  }
  delegate;
  now = Date.now;
  runningCount = 0;
  taskQueue = [];
  maxConcurrency;
  constructor(delegate = asyncScheduler, maxConcurrency = 1) {
    this.delegate = delegate;
    this.maxConcurrency = Math.max(1, maxConcurrency);
  }
  schedule(work, delay2 = 0, state) {
    const scheduleWithToken = /* @__PURE__ */ __name(() => {
      this.runningCount++;
      let released = false;
      const release = /* @__PURE__ */ __name(() => {
        if (released) return;
        released = true;
        this.runningCount--;
        this.processQueue();
      }, "release");
      const wrappedWork = /* @__PURE__ */ __name(function(innerState) {
        try {
          work.call(this, innerState);
        } finally {
          release();
        }
      }, "wrappedWork");
      const innerSub = this.delegate.schedule(wrappedWork, delay2, state);
      innerSub.add(release);
      return innerSub;
    }, "scheduleWithToken");
    if (this.runningCount < this.maxConcurrency) {
      return scheduleWithToken();
    }
    let queued = true;
    const subscription = new Subscription(() => {
      if (!queued) return;
      const idx = this.taskQueue.indexOf(executeTask);
      if (idx >= 0) this.taskQueue.splice(idx, 1);
      queued = false;
    });
    const executeTask = /* @__PURE__ */ __name(() => {
      if (subscription.closed) return;
      queued = false;
      subscription.add(scheduleWithToken());
    }, "executeTask");
    this.taskQueue.push(executeTask);
    return subscription;
  }
  processQueue() {
    while (this.runningCount < this.maxConcurrency && this.taskQueue.length > 0) {
      const task = this.taskQueue.shift();
      if (task) {
        task();
      }
    }
  }
  /**
   * 设置并发度
   */
  setConcurrency(maxConcurrency) {
    this.maxConcurrency = Math.max(1, maxConcurrency);
    this.processQueue();
  }
  /**
   * 获取当前状态
   */
  getStatus() {
    return {
      running: this.runningCount,
      queued: this.taskQueue.length,
      max: this.maxConcurrency
    };
  }
};
var DEFAULT_CRAWLER_CONCURRENCY = 1;
var crawlerScheduler = new ConcurrencyScheduler(asyncScheduler, DEFAULT_CRAWLER_CONCURRENCY);
export {
  Ast,
  AudioAst,
  BooleanAst,
  BooleanAstVisitor,
  CollectorAst,
  CollectorVisitor,
  Compiler,
  ConcurrencyScheduler,
  DEFAULT_VISITOR,
  DERIVED_INPUT,
  DERIVED_OUTPUT,
  DateAst,
  DefaultVisitor,
  DynamicNodeRegistry,
  EDGE_MODE_STRATEGY,
  EVENT_STORE,
  EdgeMode,
  providers as EdgeModeStrategyProviders,
  FilterAst,
  FilterAstVisitor,
  HANDLER_METHOD,
  Handler,
  IS_BUFFER,
  IS_MULTI,
  ImageAst,
  Input,
  LoopAst,
  MarkdownAst,
  MarkdownAstTool,
  MarkdownAstVisitor,
  MemoryEventStore,
  MergeAst,
  MqPullAst,
  MqPushAst,
  NODE,
  NoRetryError,
  Node,
  NodeExecutor,
  NotAst,
  NotAstVisitor,
  Output,
  PREVIEW_METHOD,
  PassThroughAst,
  PassThroughAstVisitor,
  Preview,
  RENDER_METHOD,
  ROUTE_SKIPPED,
  Render,
  SETTING_METHOD,
  Setting,
  State,
  StoreGetAst,
  StoreSetAst,
  SwitchAst,
  SwitchAstVisitor,
  TextAreaAst,
  TextAreaAstTool,
  TextAreaAstVisitor,
  VideoAst,
  VisitorExecutor,
  WorkflowEventStream,
  WorkflowGraphAst,
  WorkflowGraphAstVisitor,
  WorkflowRuntime,
  addConditionalEdge,
  addEdge,
  addEdges,
  addNode,
  addNodes,
  cleanOrphanedProperties,
  clearGraph,
  clone,
  crawlerScheduler,
  createWorkflowGraphAst,
  executeAst,
  executeAstWithWorkflowGraph,
  executeNodeIsolated,
  executeWorkflow,
  executeWorkflowImmediate,
  extractEndNodeOutputs,
  extractSubjectValue,
  findNodeType,
  fromJson,
  generateId,
  getAllNodeTypes,
  getAstDeepError,
  getAstErrorDescription,
  getEdgeById,
  getEdgesByNode,
  getEntryNodeInputs,
  getExposedInputs,
  getExposedOutputs,
  getInputMetadata,
  getNodeById,
  getNodesByType,
  getStateMetadata,
  getToolMethods,
  globalRuntime,
  hasBufferMode,
  hasEdge,
  hasMultiMode,
  hasNode,
  hasTool,
  isINode,
  isNode,
  isObservable,
  isRouteSkipped,
  isWorkflowGroup,
  removeEdge,
  removeEdgesByNode,
  removeNode,
  removeNodes,
  resetNodeToDefaults,
  resolveConstructor,
  serializeToString,
  setAstError,
  setGlobalRuntime,
  toJson,
  updateEdge,
  updateEdgeCondition,
  updateNode,
  updateNodePosition,
  v4,
  validateGraph
};
//# sourceMappingURL=index.mjs.map